import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import '../styles/AuditChaufferieVertical.css';

/**
 * Composant AuditChaufferie - Version Verticale CORRIGÉE
 * Formulaire interactif pour l'audit de chaufferie en une seule page
 */

function AuditChaufferieVertical() {
  const [optionalSectionOpen, setOptionalSectionOpen] = useState(false);
  const [photoMenu, setPhotoMenu] = useState(null);
  const navigate = useNavigate();

  const handlePhotoClick = (cameraId, galleryId) => {
    const isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);
    if (isMobile) {
      setPhotoMenu({ cameraId, galleryId });
    } else {
      document.getElementById(galleryId).click();
    }
  };
  const [showTooltip, setShowTooltip] = useState({});
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [sectionPositions, setSectionPositions] = useState({});
  const [validationErrors, setValidationErrors] = useState({});
  const [showValidationModal, setShowValidationModal] = useState(false);
  const [activeTooltip, setActiveTooltip] = useState(null);
  const [activeSection, setActiveSection] = useState('site');
  const [scrollProgress, setScrollProgress] = useState(0);
  const [isLocating, setIsLocating] = useState(false);
  const sectionRefs = {
    site: React.useRef(null),
    chaudiere: React.useRef(null),
    circuits: React.useRef(null),
    ecs: React.useRef(null),
    armoire: React.useRef(null),
    compteurs: React.useRef(null)
  };

  
  const [formData, setFormData] = useState({
    site: {
      nomSite: '',
      adresse: '',
      codePostal: '',
      ville: '',
      contactNom: '',
      contactTel: '',
      contactEmail: '',
      photosArmoire: []
    },
    chaudiere: {
      type: '',
      energie: '',
      energieAutre: '',
      typePilotage: '',
      plusieursChaudieres: false,
      estCascade: false,
      gererCascade: false,
      nombreChaudieres: 1,
      observations: '',
      photos: [],
      photoPlaqueSignaletique: [],
      photoChaudiereHaut: [],
      photoChaudiereBas: [],
      photoEchangeurPlaque: [],
      photoEchangeurHaut: [],
      photoEchangeurBas: []
    },
    circuits: [
      {
        id: 1,
        nom: 'Circuit 1',
        vanne: '',
        pompe: '',
        autre: '',
        photos: [],
        photoVanne: [],
        photoPompe: []
      }
    ],
    ecs: {
      preparateur: false,
      observationsPreparateur: '',
      typeEcs: '',
      pompePrimaire: '',
      pompeRecyclage: '',
      observations: '',
      photos: [],
      photoSystemeEcs: [],
      photoPompePrimaire: [],
      photoPompeRecyclage: []
    },
    armoireElectriquePhotos: [],
    armoireElectriqueInterieurPhotos: [],
    compteurs: {
      gaz:    { localisation: '', distance: '', cableRelier: '', priseDisponible: '', dejaConnecte: '', photos: [] },
      eau:    { distance: '', cableRelier: '', dejaConnecte: '', photos: [] },
      energie:{ distance: '', cableRelier: '', dejaConnecte: '', photos: [] },
      cpcu:   { distance: '', cableRelier: '', dejaConnecte: '', photos: [] }
    }
  });
  const showCompteurGaz     = formData.chaudiere.energie === 'gaz';
  const showCompteurEau     = ['fioul', 'urbain', 'autre', 'gaz'].includes(formData.chaudiere.energie);
  const showCompteurEnergie = ['fioul', 'urbain', 'autre', 'gaz'].includes(formData.chaudiere.energie);
  const showCompteurCpcu    = formData.chaudiere.energie === 'urbain';

  
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['site', 'chaudiere', 'circuits', 'ecs', 'armoire', 'compteurs'];
      const scrollPosition = window.scrollY + 200; // Offset pour le header
      
      // Calculer la progression globale
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      const progress = (window.scrollY / docHeight) * 100;
      setScrollProgress(Math.min(progress, 100));
      
      // Déterminer quelle section est visible
      for (const sectionId of sections) {
        const element = document.getElementById(`section-${sectionId}`);
        if (element) {
          const rect = element.getBoundingClientRect();
          const offset = window.innerHeight / 3;
          
          if (rect.top <= offset && rect.bottom >= offset) {
            setActiveSection(sectionId);
            break;
          }
        }
      }
    };
    
    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Init
    
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
    
  const scrollToSection = (sectionId) => {
    const element = document.getElementById(`section-${sectionId}`);
    if (element) {
      const headerOffset = 180; // Height du header + mini-navbar
      const elementPosition = element.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - headerOffset;
      
      window.scrollTo({
        top: offsetPosition,
        behavior: 'smooth'
      });
    }
  };

  
  const reinitialiserFormulaire = () => {
    if (window.confirm('⚠️ Êtes-vous sûr de vouloir réinitialiser toutes les données ?\n\nToutes les informations saisies et les photos seront perdues.')) {
      // Réinitialiser le formulaire
      setFormData({
        site: {
          nomSite: '',
          adresse: '',
          codePostal: '',
          ville: '',
          contactNom: '',
          contactTel: '',
          contactEmail: '',
          photosArmoire: []
        },
        chaudiere: {
          type: '',
          energie: '',
          energieAutre: '',
          typePilotage: '',
          plusieursChaudieres: false,
          estCascade: false,
          gererCascade: false,
          nombreChaudieres: 1,
          observations: '',
          photos: [],
          photoPlaqueSignaletique: [],
          photoChaudiereHaut: [],
          photoChaudiereBas: [],
          photoEchangeurPlaque: [],
          photoEchangeurHaut: [],
          photoEchangeurBas: []
        },
        circuits: [
          {
            id: 1,
            nom: 'Circuit 1',
            vanne: '',
            pompe: '',
            autre: '',
            photos: [],
            photoVanne: [],
            photoPompe: []
          }
        ],
        ecs: {
          preparateur: false,
          observationsPreparateur: '',
          typeEcs: '',
          pompePrimaire: '',
          pompeRecyclage: '',
          observations: '',
          photos: [],
          photoSystemeEcs: [],
          photoPompePrimaire: [],
          photoPompeRecyclage: []
        },
        armoireElectriquePhotos: [],
        armoireElectriqueInterieurPhotos: [],
        compteurs: {
          gaz:    { localisation: '', distance: '', cableRelier: '', priseDisponible: '', dejaConnecte: '', photos: [] },
          eau:    { distance: '', cableRelier: '', dejaConnecte: '', photos: [] },
          energie:{ distance: '', cableRelier: '', dejaConnecte: '', photos: [] },
          cpcu:   { distance: '', cableRelier: '', dejaConnecte: '', photos: [] }
        }
      });

      // Supprimer le brouillon
      localStorage.removeItem('audit_chaufferie_draft');
      
      // Réinitialiser les erreurs
      setValidationErrors({});
      
      // Scroller vers le haut
      window.scrollTo({ top: 0, behavior: 'smooth' });
      
      alert('✅ Formulaire réinitialisé avec succès !');
    }
  };
  const remplirPositionActuelle = () => {
    if (!navigator.geolocation) {
      alert('La géolocalisation n\'est pas supportée par votre navigateur.');
      return;
    }
    setIsLocating(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        try {
          const { latitude, longitude } = position.coords;
          const response = await fetch(
            `https://nominatim.openstreetmap.org/reverse?format=json&lat=${latitude}&lon=${longitude}&addressdetails=1`,
            { headers: { 'Accept-Language': 'fr' } }
          );
          const data = await response.json();
          const addr = data.address || {};
          const numero = addr.house_number || '';
          const rue = addr.road || addr.pedestrian || addr.footway || '';
          const adresse = [numero, rue].filter(Boolean).join(' ');
          const codePostal = addr.postcode || '';
          const ville = addr.city || addr.town || addr.village || addr.municipality || '';
          setFormData(prev => ({
            ...prev,
            site: {
              ...prev.site,
              adresse: adresse || prev.site.adresse,
              codePostal: codePostal || prev.site.codePostal,
              ville: ville || prev.site.ville
            }
          }));
        } catch {
          alert('Erreur lors de la récupération de l\'adresse. Vérifiez votre connexion internet.');
        } finally {
          setIsLocating(false);
        }
      },
      (error) => {
        setIsLocating(false);
        if (error.code === error.PERMISSION_DENIED) {
          alert('Accès à la localisation refusé. Veuillez autoriser la géolocalisation dans votre navigateur.');
        } else if (error.code === error.POSITION_UNAVAILABLE) {
          alert('Position non disponible. Vérifiez que la géolocalisation est activée sur votre appareil.');
        } else {
          alert('Impossible d\'obtenir votre position.');
        }
      },
      { enableHighAccuracy: true, timeout: 10000 }
    );
  };

  // Données des équipements avec chemins d'images
  const equipements = {
    energies: [
      { id: 'fioul', nom: 'Fioul', icon: '🛢️', image: 'images/audit/energies/fioul.png' },
      { id: 'gaz', nom: 'Gaz', icon: '🔥', image: 'images/audit/energies/gaz.png' },
      { id: 'urbain', nom: 'Chauffage Urbain', icon: '🏭', image: 'images/audit/energies/urbain.png' },
      { id: 'autre', nom: 'Autre', icon: '⚡', image: 'images/audit/energies/autre.png' }
    ],
    pilotages: [
      { id: '1-allure', nom: '1 allure', description: 'Tout ou rien', tooltip: 'Chaudière simple sans modulation', image: 'images/audit/pilotages/1-allure.png' },
      { id: '2-allures', nom: '2 allures', description: 'Deux vitesses', tooltip: 'Chaudière avec 2 niveaux de puissance', image: 'images/audit/pilotages/2-allures.png' },
      { id: '0-10v', nom: 'Modulation 0-10V', description: 'Analogique', tooltip: 'Modulation par signal 0-10V', image: 'images/audit/pilotages/0-10v.png' },
      { id: '3-points', nom: 'Modulation 3 points', description: 'Ouverture/Fermeture', tooltip: 'Modulation par impulsions 3 points', image: 'images/audit/pilotages/3-points.png' },
      { id: 'consigne-0-10v', nom: 'Consigne 0-10V', description: 'Consigne eau', tooltip: 'Régulation par consigne de température eau', image: 'images/audit/pilotages/consigne-0-10v.png' }
    ],
    vannes: [
      { id: 'v3v-230v', nom: 'V3V 230V', description: 'Vanne 3 voies TOR', tooltip: 'Vanne motorisée 230V tout ou rien', image: 'images/audit/vannes/v3v-230v.png' },
      { id: 'v3v-0-10v', nom: 'V3V 0-10V', description: 'Vanne 3 voies modulante', tooltip: 'Vanne modulante proportionnelle', image: 'images/audit/vannes/v3v-0-10v.png' },
      { id: 'v2v-tor', nom: 'V2V TOR', description: 'Vanne 2 voies tout ou rien', tooltip: 'Vanne on/off simple', image: 'images/audit/vannes/v2v-tor.png' },
      { id: 'constant', nom: 'Débit constant', description: 'Sans vanne', tooltip: 'Circuit à débit fixe', image: 'images/audit/vannes/constant.png' }
    ],
    pompes: [
      { id: 'simple', nom: 'Pompe simple', description: 'Une seule pompe', tooltip: 'Circulation simple', image: 'images/audit/pompes/simple.png' },
      { id: 'double', nom: 'Double pompe', description: 'Deux pompes', tooltip: 'Deux pompes en parallèle', image: 'images/audit/pompes/double.png' },
      { id: 'permutation', nom: 'Permutation auto', description: 'Redondance', tooltip: 'Permutation automatique pour fiabilité', image: 'images/audit/pompes/permutation.png' }
    ],
    pilotagesEchangeur: [
      { id: 'vanne-230v', nom: 'Vanne 230V', description: 'Vanne motorisée 230V', tooltip: 'Pilotage par vanne 230V' },
      { id: 'vanne-0-10v', nom: 'Vanne 0-10V', description: 'Vanne modulante', tooltip: 'Pilotage par vanne 0-10V' },
      { id: 'vanne-detente', nom: 'Vanne Détente', description: 'Vanne de détente', tooltip: 'Pilotage par vanne de détente' },
      { id: 'vanne-condensat', nom: 'Vanne Condensat', description: 'Vanne condensat', tooltip: 'Pilotage par vanne condensat' }
    ],
    typesEcs: [
      { id: 'stockage-primaire', nom: 'Stockage Primaire', description: 'Ballon de stockage primaire', tooltip: 'Stockage d\'eau chaude avec production primaire' },
      { id: 'semi-instantane', nom: 'Semi Instantané', description: 'Production semi-instantanée', tooltip: 'Production d\'ECS avec petit ballon tampon' },
      { id: 'ballon-ecs', nom: 'Ballon ECS', description: 'Ballon eau chaude sanitaire', tooltip: 'Ballon de stockage ECS classique' },
      { id: 'depart-direct', nom: 'Départ Direct', description: 'Production instantanée', tooltip: 'Production directe sans stockage' }
    ],
    pompesEcs: [
      { id: 'simple', nom: 'Pompe Simple', description: 'Une seule pompe' },
      { id: 'double', nom: 'Pompe Double', description: 'Deux pompes' },
      { id: 'autonome', nom: 'Pompe Autonome', description: 'Pompe autonome' }
    ]
  };

  // Auto-save dans localStorage
  useEffect(() => {
    const timer = setTimeout(() => {
      localStorage.setItem('audit_chaufferie_draft', JSON.stringify(formData));
    }, 1000);
    return () => clearTimeout(timer);
  }, [formData]);

  // Détection du statut réseau
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Charger le brouillon au démarrage
  useEffect(() => {
    const draft = localStorage.getItem('audit_chaufferie_draft');
    if (draft) {
      try {
        const loadedData = JSON.parse(draft);
        setFormData({
          ...loadedData,
          site: {
            ...loadedData.site,
            photosArmoire: loadedData.site?.photosArmoire || []
          },
          chaudiere: {
            ...loadedData.chaudiere,
            photos: loadedData.chaudiere?.photos || [],
            photoPlaqueSignaletique: loadedData.chaudiere?.photoPlaqueSignaletique || [],
            photoChaudiereHaut: loadedData.chaudiere?.photoChaudiereHaut || [],
            photoChaudiereBas: loadedData.chaudiere?.photoChaudiereBas || [],
            photoEchangeurPlaque: loadedData.chaudiere?.photoEchangeurPlaque || [],
            photoEchangeurHaut: loadedData.chaudiere?.photoEchangeurHaut || [],
            photoEchangeurBas: loadedData.chaudiere?.photoEchangeurBas || []
          },
          circuits: loadedData.circuits?.map(c => ({
            ...c,
            photos: c.photos || [],
            photoVanne: c.photoVanne || [],
            photoPompe: c.photoPompe || []
          })) || [],
          ecs: {
            ...loadedData.ecs,
            photos: loadedData.ecs?.photos || [],
            photoSystemeEcs: loadedData.ecs?.photoSystemeEcs || [],
            photoPompePrimaire: loadedData.ecs?.photoPompePrimaire || [],
            photoPompeRecyclage: loadedData.ecs?.photoPompeRecyclage || []
          },
          armoireElectriquePhotos: loadedData.armoireElectriquePhotos || [],
          armoireElectriqueInterieurPhotos: loadedData.armoireElectriqueInterieurPhotos || [],
          compteurs: {
            gaz: {
              localisation:    loadedData.compteurs?.gaz?.localisation    || '',
              distance:        loadedData.compteurs?.gaz?.distance        || '',
              cableRelier:     loadedData.compteurs?.gaz?.cableRelier     || '',
              priseDisponible: loadedData.compteurs?.gaz?.priseDisponible || '',
              dejaConnecte:    loadedData.compteurs?.gaz?.dejaConnecte    || '',
              photos:          loadedData.compteurs?.gaz?.photos          || []
            },
            eau: {
              distance:    loadedData.compteurs?.eau?.distance    || '',
              cableRelier: loadedData.compteurs?.eau?.cableRelier || '',
              dejaConnecte:loadedData.compteurs?.eau?.dejaConnecte|| '',
              photos:      loadedData.compteurs?.eau?.photos      || []
            },
            energie: {
              distance:    loadedData.compteurs?.energie?.distance    || '',
              cableRelier: loadedData.compteurs?.energie?.cableRelier || '',
              dejaConnecte:loadedData.compteurs?.energie?.dejaConnecte|| '',
              photos:      loadedData.compteurs?.energie?.photos      || []
            },
            cpcu: {
              distance:    loadedData.compteurs?.cpcu?.distance    || '',
              cableRelier: loadedData.compteurs?.cpcu?.cableRelier || '',
              dejaConnecte:loadedData.compteurs?.cpcu?.dejaConnecte|| '',
              photos:      loadedData.compteurs?.cpcu?.photos      || []
            }
          }
        });
      } catch (e) {
        console.error('Erreur chargement brouillon:', e);
      }
    }
  }, []);

  // Calculer les positions des sections pour les accolades
  useEffect(() => {
    const updatePositions = () => {
      const positions = {};
      Object.keys(sectionRefs).forEach(key => {
        if (sectionRefs[key].current) {
          const rect = sectionRefs[key].current.getBoundingClientRect();
          positions[key] = {
            top: sectionRefs[key].current.offsetTop,
            height: rect.height
          };
        }
      });
      setSectionPositions(positions);
    };

    updatePositions();
    window.addEventListener('resize', updatePositions);
    
    const observer = new MutationObserver(updatePositions);
    Object.values(sectionRefs).forEach(ref => {
      if (ref.current) {
        observer.observe(ref.current, { childList: true, subtree: true });
      }
    });

    return () => {
      window.removeEventListener('resize', updatePositions);
      observer.disconnect();
    };
  }, [formData]);

  // Fonction de compression d'image - IDENTIQUE À LA VERSION DE BASE
  const compressImage = (file, maxWidth = 1200, quality = 0.8) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(file);
      reader.onload = (event) => {
        const img = new Image();
        img.src = event.target.result;
        img.onload = () => {
          const canvas = document.createElement('canvas');
          let width = img.width;
          let height = img.height;

          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }

          canvas.width = width;
          canvas.height = height;
          const ctx = canvas.getContext('2d');
          ctx.drawImage(img, 0, 0, width, height);

          canvas.toBlob(
            (blob) => {
              resolve({
                blob,
                dataUrl: canvas.toDataURL('image/jpeg', quality)
              });
            },
            'image/jpeg',
            quality
          );
        };
        img.onerror = reject;
      };
      reader.onerror = reject;
    });
  };

  // Upload de photos - IDENTIQUE À LA VERSION DE BASE
  const handlePhotoUpload = (section, circuitId = null) => async (event) => {
    const files = Array.from(event.target.files);

    for (const file of files) {
      if (file.size > 10 * 1024 * 1024) {
        alert(`Le fichier ${file.name} est trop volumineux (max 10MB)`);
        continue;
      }

      try {
        const { dataUrl } = await compressImage(file);

        const photo = {
          id: Date.now() + Math.random(),
          name: file.name,
          dataUrl: dataUrl,
          uploadDate: new Date().toISOString()
        };

        if (section === 'site') {
          setFormData(prev => ({
            ...prev,
            site: {
              ...prev.site,
              photosArmoire: [...prev.site.photosArmoire, photo]
            }
          }));
        } else if (section === 'chaudiere') {
          setFormData(prev => ({
            ...prev,
            chaudiere: {
              ...prev.chaudiere,
              photos: [...prev.chaudiere.photos, photo]
            }
          }));
        } else if (section === 'photoPlaqueSignaletique') {
          setFormData(prev => ({
            ...prev,
            chaudiere: {
              ...prev.chaudiere,
              photoPlaqueSignaletique: [...prev.chaudiere.photoPlaqueSignaletique, photo]
            }
          }));
        } else if (section === 'photoChaudiereHaut') {
          setFormData(prev => ({
            ...prev,
            chaudiere: {
              ...prev.chaudiere,
              photoChaudiereHaut: [...prev.chaudiere.photoChaudiereHaut, photo]
            }
          }));
        } else if (section === 'photoChaudiereBas') {
          setFormData(prev => ({
            ...prev,
            chaudiere: {
              ...prev.chaudiere,
              photoChaudiereBas: [...prev.chaudiere.photoChaudiereBas, photo]
            }
          }));
        } else if (section === 'photoEchangeurPlaque') {
          setFormData(prev => ({
            ...prev,
            chaudiere: {
              ...prev.chaudiere,
              photoEchangeurPlaque: [...prev.chaudiere.photoEchangeurPlaque, photo]
            }
          }));
        } else if (section === 'photoEchangeurHaut') {
          setFormData(prev => ({
            ...prev,
            chaudiere: {
              ...prev.chaudiere,
              photoEchangeurHaut: [...prev.chaudiere.photoEchangeurHaut, photo]
            }
          }));
        } else if (section === 'photoEchangeurBas') {
          setFormData(prev => ({
            ...prev,
            chaudiere: {
              ...prev.chaudiere,
              photoEchangeurBas: [...prev.chaudiere.photoEchangeurBas, photo]
            }
          }));
        } else if (section === 'ecs') {
          setFormData(prev => ({
            ...prev,
            ecs: {
              ...prev.ecs,
              photos: [...prev.ecs.photos, photo]
            }
          }));
        } else if (section === 'photoSystemeEcs') {
          setFormData(prev => ({
            ...prev,
            ecs: {
              ...prev.ecs,
              photoSystemeEcs: [...prev.ecs.photoSystemeEcs, photo]
            }
          }));
        } else if (section === 'photoPompePrimaire') {
          setFormData(prev => ({
            ...prev,
            ecs: {
              ...prev.ecs,
              photoPompePrimaire: [...prev.ecs.photoPompePrimaire, photo]
            }
          }));
        } else if (section === 'photoPompeRecyclage') {
          setFormData(prev => ({
            ...prev,
            ecs: {
              ...prev.ecs,
              photoPompeRecyclage: [...prev.ecs.photoPompeRecyclage, photo]
            }
          }));
        } else if (section === 'circuit' && circuitId) {
          setFormData(prev => ({
            ...prev,
            circuits: prev.circuits.map(c =>
              c.id === circuitId
                ? { ...c, photos: [...c.photos, photo] }
                : c
            )
          }));
        } else if (section === 'circuitVanne' && circuitId) {
          setFormData(prev => ({
            ...prev,
            circuits: prev.circuits.map(c =>
              c.id === circuitId
                ? { ...c, photoVanne: [...(c.photoVanne || []), photo] }
                : c
            )
          }));
        } else if (section === 'circuitPompe' && circuitId) {
          setFormData(prev => ({
            ...prev,
            circuits: prev.circuits.map(c =>
              c.id === circuitId
                ? { ...c, photoPompe: [...(c.photoPompe || []), photo] }
                : c
            )
          }));
        } else if (section === 'armoireElectrique') {
          setFormData(prev => ({
            ...prev,
            armoireElectriquePhotos: [...prev.armoireElectriquePhotos, photo]
          }));
        } else if (section === 'armoireElectriqueInterieur') {
          setFormData(prev => ({
            ...prev,
            armoireElectriqueInterieurPhotos: [...prev.armoireElectriqueInterieurPhotos, photo]
          }));
        } else if (section === 'compteurGaz') {
          setFormData(prev => ({
            ...prev,
            compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, photos: [...prev.compteurs.gaz.photos, photo] } }
          }));
        } else if (section === 'compteurEau') {
          setFormData(prev => ({
            ...prev,
            compteurs: { ...prev.compteurs, eau: { ...prev.compteurs.eau, photos: [...prev.compteurs.eau.photos, photo] } }
          }));
        } else if (section === 'compteurEnergie') {
          setFormData(prev => ({
            ...prev,
            compteurs: { ...prev.compteurs, energie: { ...prev.compteurs.energie, photos: [...prev.compteurs.energie.photos, photo] } }
          }));
        } else if (section === 'compteurCpcu') {
          setFormData(prev => ({
            ...prev,
            compteurs: { ...prev.compteurs, cpcu: { ...prev.compteurs.cpcu, photos: [...prev.compteurs.cpcu.photos, photo] } }
          }));
        }
      } catch (error) {
        console.error('Erreur compression image:', error);
        alert(`Erreur lors du traitement de ${file.name}`);
      }
    }
  };

  // Suppression de photo - IDENTIQUE À LA VERSION DE BASE
  const supprimerPhoto = (section, photoId, circuitId = null) => {
    if (section === 'site') {
      setFormData(prev => ({
        ...prev,
        site: {
          ...prev.site,
          photosArmoire: prev.site.photosArmoire.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'chaudiere') {
      setFormData(prev => ({
        ...prev,
        chaudiere: {
          ...prev.chaudiere,
          photos: prev.chaudiere.photos.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoPlaqueSignaletique') {
      setFormData(prev => ({
        ...prev,
        chaudiere: {
          ...prev.chaudiere,
          photoPlaqueSignaletique: prev.chaudiere.photoPlaqueSignaletique.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoChaudiereHaut') {
      setFormData(prev => ({
        ...prev,
        chaudiere: {
          ...prev.chaudiere,
          photoChaudiereHaut: prev.chaudiere.photoChaudiereHaut.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoChaudiereBas') {
      setFormData(prev => ({
        ...prev,
        chaudiere: {
          ...prev.chaudiere,
          photoChaudiereBas: prev.chaudiere.photoChaudiereBas.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoEchangeurPlaque') {
      setFormData(prev => ({
        ...prev,
        chaudiere: {
          ...prev.chaudiere,
          photoEchangeurPlaque: prev.chaudiere.photoEchangeurPlaque.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoEchangeurHaut') {
      setFormData(prev => ({
        ...prev,
        chaudiere: {
          ...prev.chaudiere,
          photoEchangeurHaut: prev.chaudiere.photoEchangeurHaut.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoEchangeurBas') {
      setFormData(prev => ({
        ...prev,
        chaudiere: {
          ...prev.chaudiere,
          photoEchangeurBas: prev.chaudiere.photoEchangeurBas.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'ecs') {
      setFormData(prev => ({
        ...prev,
        ecs: {
          ...prev.ecs,
          photos: prev.ecs.photos.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoSystemeEcs') {
      setFormData(prev => ({
        ...prev,
        ecs: {
          ...prev.ecs,
          photoSystemeEcs: prev.ecs.photoSystemeEcs.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoPompePrimaire') {
      setFormData(prev => ({
        ...prev,
        ecs: {
          ...prev.ecs,
          photoPompePrimaire: prev.ecs.photoPompePrimaire.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'photoPompeRecyclage') {
      setFormData(prev => ({
        ...prev,
        ecs: {
          ...prev.ecs,
          photoPompeRecyclage: prev.ecs.photoPompeRecyclage.filter(p => p.id !== photoId)
        }
      }));
    } else if (section === 'circuit' && circuitId) {
      setFormData(prev => ({
        ...prev,
        circuits: prev.circuits.map(c =>
          c.id === circuitId
            ? { ...c, photos: c.photos.filter(p => p.id !== photoId) }
            : c
        )
      }));
    } else if (section === 'circuitVanne' && circuitId) {
      setFormData(prev => ({
        ...prev,
        circuits: prev.circuits.map(c =>
          c.id === circuitId
            ? { ...c, photoVanne: (c.photoVanne || []).filter(p => p.id !== photoId) }
            : c
        )
      }));
    } else if (section === 'circuitPompe' && circuitId) {
      setFormData(prev => ({
        ...prev,
        circuits: prev.circuits.map(c =>
          c.id === circuitId
            ? { ...c, photoPompe: (c.photoPompe || []).filter(p => p.id !== photoId) }
            : c
        )
      }));
    } else if (section === 'armoireElectrique') {
      setFormData(prev => ({
        ...prev,
        armoireElectriquePhotos: prev.armoireElectriquePhotos.filter(p => p.id !== photoId)
      }));
    } else if (section === 'armoireElectriqueInterieur') {
      setFormData(prev => ({
        ...prev,
        armoireElectriqueInterieurPhotos: prev.armoireElectriqueInterieurPhotos.filter(p => p.id !== photoId)
      }));
    } else if (section === 'compteurGaz') {
      setFormData(prev => ({
        ...prev,
        compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, photos: prev.compteurs.gaz.photos.filter(p => p.id !== photoId) } }
      }));
    } else if (section === 'compteurEau') {
      setFormData(prev => ({
        ...prev,
        compteurs: { ...prev.compteurs, eau: { ...prev.compteurs.eau, photos: prev.compteurs.eau.photos.filter(p => p.id !== photoId) } }
      }));
    } else if (section === 'compteurEnergie') {
      setFormData(prev => ({
        ...prev,
        compteurs: { ...prev.compteurs, energie: { ...prev.compteurs.energie, photos: prev.compteurs.energie.photos.filter(p => p.id !== photoId) } }
      }));
    } else if (section === 'compteurCpcu') {
      setFormData(prev => ({
        ...prev,
        compteurs: { ...prev.compteurs, cpcu: { ...prev.compteurs.cpcu, photos: prev.compteurs.cpcu.photos.filter(p => p.id !== photoId) } }
      }));
    }
  };

  const ajouterCircuit = () => {
    const newId = Math.max(...formData.circuits.map(c => c.id), 0) + 1;
    setFormData(prev => ({
      ...prev,
      circuits: [...prev.circuits, {
        id: newId,
        nom: `Circuit ${newId}`,
        vanne: '',
        pompe: '',
        autre: '',
        photos: [],
        photoVanne: [],
        photoPompe: []
      }]
    }));
  };

  const ajusterNombreCircuits = (nombre) => {
    const n = Math.max(1, nombre);
    setFormData(prev => {
      const current = prev.circuits;
      if (n === current.length) return prev;
      if (n > current.length) {
        const nouveaux = [];
        let maxId = Math.max(...current.map(c => c.id), 0);
        for (let i = 0; i < n - current.length; i++) {
          maxId++;
          nouveaux.push({
            id: maxId,
            nom: `Circuit ${maxId}`,
            vanne: '',
            pompe: '',
            autre: '',
            photos: [],
            photoVanne: [],
            photoPompe: []
          });
        }
        return { ...prev, circuits: [...current, ...nouveaux] };
      }
      return { ...prev, circuits: current.slice(0, n) };
    });
  };

  const supprimerCircuit = (id) => {
    if (formData.circuits.length <= 1) {
      alert('Au moins un circuit doit être conservé');
      return;
    }
    setFormData(prev => ({
      ...prev,
      circuits: prev.circuits.filter(c => c.id !== id)
    }));
  };

  const renommerCircuit = (id, nouveauNom) => {
    setFormData(prev => ({
      ...prev,
      circuits: prev.circuits.map(c =>
        c.id === id ? { ...c, nom: nouveauNom } : c
      )
    }));
  };

  // const validerFormulaire = () => {
  //   
  //   return true;
  // };
  const validerFormulaire = () => {
  const errors = {};
  if (!formData.site.nomSite || !formData.site.ville) {
      errors.site = "Veuillez renseigner au minimum le nom du site et la ville";
    }
    if (!formData.site.contactEmail && !formData.site.contactTel) {
      errors.contact = "Veuillez renseigner au moins un moyen de contact";
    }
  // // 1. Photo du synoptique (Section Site)
  // if (!formData.site.photosArmoire || formData.site.photosArmoire.length === 0) {
  //   errors.synoptique = "Photo du synoptique obligatoire";
  // }

  // // 2. Photos chaudière selon le type d'énergie
  // if (formData.chaudiere.energie === 'fioul' || formData.chaudiere.energie === 'gaz') {
  //   if (!formData.chaudiere.photoPlaqueSignaletique || formData.chaudiere.photoPlaqueSignaletique.length === 0) {
  //     errors.photoPlaqueSignaletique = "Photo plaque signalétique obligatoire";
  //   }
  //   if (!formData.chaudiere.photoChaudiereHaut || formData.chaudiere.photoChaudiereHaut.length === 0) {
  //     errors.photoChaudiereHaut = "Photo chaudière en haut obligatoire";
  //   }
  //   if (!formData.chaudiere.photoChaudiereBas || formData.chaudiere.photoChaudiereBas.length === 0) {
  //     errors.photoChaudiereBas = "Photo chaudière en bas obligatoire";
  //   }
  // }

  // if (formData.chaudiere.energie === 'urbain') {
  //   if (!formData.chaudiere.photoEchangeurPlaque || formData.chaudiere.photoEchangeurPlaque.length === 0) {
  //     errors.photoEchangeurPlaque = "Photo plaque signalétique échangeur obligatoire";
  //   }
  //   if (!formData.chaudiere.photoEchangeurHaut || formData.chaudiere.photoEchangeurHaut.length === 0) {
  //     errors.photoEchangeurHaut = "Photo échangeur en haut obligatoire";
  //   }
  //   if (!formData.chaudiere.photoEchangeurBas || formData.chaudiere.photoEchangeurBas.length === 0) {
  //     errors.photoEchangeurBas = "Photo échangeur en bas obligatoire";
  //   }
  // }

  // // 3. Photos circuits
  // formData.circuits.forEach((circuit, index) => {
  //   if (!circuit.photoVanne || circuit.photoVanne.length === 0) {
  //     errors[`circuit${circuit.id}Vanne`] = `Photo vanne du ${circuit.nom} obligatoire`;
  //   }
  //   if (!circuit.photoPompe || circuit.photoPompe.length === 0) {
  //     errors[`circuit${circuit.id}Pompe`] = `Photo pompe du ${circuit.nom} obligatoire`;
  //   }
  // });

  // // 4. Photos ECS
  // if (!formData.ecs.photoSystemeEcs || formData.ecs.photoSystemeEcs.length === 0) {
  //   errors.photoSystemeEcs = "Photo système ECS obligatoire";
  // }
  // if (!formData.ecs.photoPompePrimaire || formData.ecs.photoPompePrimaire.length === 0) {
  //   errors.photoPompePrimaire = "Photo pompe primaire ECS obligatoire";
  // }
  // if (!formData.ecs.photoPompeRecyclage || formData.ecs.photoPompeRecyclage.length === 0) {
  //   errors.photoPompeRecyclage = "Photo pompe recyclage ECS obligatoire";
  // }

  // // 5. Photos armoire électrique
  // if (!formData.armoireElectriquePhotos || formData.armoireElectriquePhotos.length === 0) {
  //   errors.armoireElectriqueAvant = "Photo armoire électrique avant obligatoire";
  // }
  // if (!formData.armoireElectriqueInterieurPhotos || formData.armoireElectriqueInterieurPhotos.length === 0) {
  //   errors.armoireElectriqueInterieur = "Photo armoire électrique intérieur obligatoire";
  // }

  setValidationErrors(errors);
  return Object.keys(errors).length === 0;
};
const isPhotoMissing = (key) => {
  return validationErrors[key] !== undefined;
};

const genererPDF = async () => {
  const doc = new jsPDF('portrait', 'mm', 'a4');
  const pageWidth = doc.internal.pageSize.getWidth();
  const pageHeight = doc.internal.pageSize.getHeight();
  const margin = 15;
  let currentY = margin;
  const maxPhotoWidth = pageWidth - (2 * margin);
  const maxPhotoHeight = 100;
  const pdpWidth = pageWidth - (2 * margin);

  // Pré-chargement de Logo2.png en base64 (fiable dans jsPDF)
  let logoBase64 = null;
  try {
    logoBase64 = await new Promise((resolve, reject) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve(canvas.toDataURL('image/png'));
      };
      img.onerror = reject;
      img.src = 'images/Logo2.png';
    });
  } catch (e) {
    console.log('Logo2.png non chargeable');
  }

  // Pré-calcul des dimensions de PDP.png pour respecter son ratio
  let pdpHeight = 15;
  let pdpBase64 = null;
  try {
    const pdpResult = await new Promise((resolve) => {
      const img = new Image();
      img.onload = () => {
        const canvas = document.createElement('canvas');
        canvas.width = img.width;
        canvas.height = img.height;
        canvas.getContext('2d').drawImage(img, 0, 0);
        resolve({ base64: canvas.toDataURL('image/png'), height: Math.round(pdpWidth / (img.width / img.height)) });
      };
      img.onerror = () => resolve({ base64: null, height: 15 });
      img.src = 'images/PDP.png';
    });
    pdpBase64 = pdpResult.base64;
    pdpHeight = pdpResult.height;
  } catch (e) {
    pdpHeight = 15;
  }
  const footerReserve = pdpHeight + 15;

  // Helper pour ajouter le logo en haut de la page courante
  const addLogoToPage = () => {
    if (logoBase64) {
      try {
        doc.addImage(logoBase64, 'PNG', margin, 5, 65, 15);
      } catch (e) {
        console.log('Logo non disponible');
      }
    }
  };

  // Helper pour nouvelle page
  const checkNeedNewPage = (spaceNeeded) => {
    if (currentY + spaceNeeded > pageHeight - footerReserve) {
      doc.addPage();
      addLogoToPage();
      currentY = 30;
    }
  };

  // Helper pour ajouter une photo avec ratio et dimensions optimisées
  const addPhoto = async (photo) => {
    try {
      const dims = await new Promise((resolve) => {
        const img = new Image();
        img.onload = () => {
          const ratio = img.width / img.height;
          let finalWidth, finalHeight;
          if (ratio > 1) {
            finalWidth = Math.min(maxPhotoWidth, 120);
            finalHeight = finalWidth / ratio;
            if (finalHeight > maxPhotoHeight) {
              finalHeight = maxPhotoHeight;
              finalWidth = finalHeight * ratio;
            }
          } else {
            finalHeight = maxPhotoHeight;
            finalWidth = finalHeight * ratio;
            if (finalWidth > maxPhotoWidth) {
              finalWidth = maxPhotoWidth;
              finalHeight = finalWidth / ratio;
            }
          }
          resolve({ width: finalWidth, height: finalHeight });
        };
        img.onerror = () => resolve({ width: 80, height: 60 });
        img.src = photo.dataUrl;
      });
      checkNeedNewPage(dims.height + 10);
      const xPosition = margin + (maxPhotoWidth - dims.width) / 2;
      doc.addImage(photo.dataUrl, 'JPEG', xPosition, currentY, dims.width, dims.height);
      currentY += dims.height + 5;
    } catch (e) {
      console.log('Erreur ajout photo:', e);
    }
  };

  // Logo sur la première page
  addLogoToPage();
  currentY = 30;

  // Titre principal et date
  const dateStr = new Date().toLocaleDateString('fr-FR');

  doc.setFontSize(20);
  doc.setTextColor(3, 76, 140);
  doc.setFont('helvetica', 'bold');
  doc.text('AUDIT CHAUFFERIE', margin, currentY);

  doc.setFontSize(10);
  doc.setTextColor(100, 100, 100);
  doc.setFont('helvetica', 'normal');
  doc.text(`Réalisé le ${dateStr}`, pageWidth - margin, currentY, { align: 'right' });

  currentY += 15;

  // Section Site
  doc.setFontSize(14);
  doc.setTextColor(3, 76, 140);
  doc.setFont('helvetica', 'bold');
  doc.text('INFORMATIONS DU SITE', margin, currentY);
  currentY += 8;

  const siteInfo = [
    ['Nom du site', formData.site.nomSite],
    ['Adresse', formData.site.adresse],
    ['Code postal', formData.site.codePostal],
    ['Ville', formData.site.ville],
    ['Contact', formData.site.contactNom],
    ['Téléphone', formData.site.contactTel],
    ['Email', formData.site.contactEmail]
  ].filter(row => row[1]);

  doc.autoTable({
    startY: currentY,
    body: siteInfo,
    theme: 'grid',
    headStyles: { fillColor: [3, 76, 140] },
    margin: { left: margin, right: margin, top: 30 },
    styles: { fontSize: 9 },
    didDrawPage: () => { addLogoToPage(); }
  });

  currentY = doc.lastAutoTable.finalY + 10;

  // Photos synoptique
  if (formData.site.photosArmoire && formData.site.photosArmoire.length > 0) {
    checkNeedNewPage(60);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Photo du synoptique:', margin, currentY);
    currentY += 8;
    for (const photo of formData.site.photosArmoire) {
      await addPhoto(photo);
    }
    currentY += 5;
  }

  // Section Chaudière
  checkNeedNewPage(50);
  doc.setFontSize(14);
  doc.setTextColor(3, 76, 140);
  doc.setFont('helvetica', 'bold');
  doc.text('CHAUDIERE / PRIMAIRE', margin, currentY);
  currentY += 8;

  const energieLibelle = formData.chaudiere.energie === 'autre' && formData.chaudiere.energieAutre
    ? `Autre (${formData.chaudiere.energieAutre})`
    : equipements.energies.find(e => e.id === formData.chaudiere.energie)?.nom || '-';

  const chaudiereInfo = [
    ['Énergie', energieLibelle],
    [formData.chaudiere.energie === 'urbain' ? 'Pilotage échangeur' : 'Type de pilotage', equipements.pilotages.find(p => p.id === formData.chaudiere.typePilotage)?.nom || equipements.pilotagesEchangeur.find(p => p.id === formData.chaudiere.typePilotage)?.nom || '-'],
    formData.chaudiere.plusieursChaudieres && ['Nombre de chaudières', formData.chaudiere.nombreChaudieres],
    formData.chaudiere.plusieursChaudieres && formData.chaudiere.estCascade && ['Cascade', 'Oui'],
    formData.chaudiere.plusieursChaudieres && formData.chaudiere.estCascade && ['Gestion cascade', formData.chaudiere.gererCascade ? 'Oui' : 'Non'],
    formData.chaudiere.observations && ['Observations', formData.chaudiere.observations]
  ].filter(Boolean);

  doc.autoTable({
    startY: currentY,
    body: chaudiereInfo,
    theme: 'grid',
    headStyles: { fillColor: [3, 76, 140] },
    margin: { left: margin, right: margin, top: 30 },
    styles: { fontSize: 9 },
    didDrawPage: () => { addLogoToPage(); }
  });

  currentY = doc.lastAutoTable.finalY + 10;

  // Photos chaudière (Fioul/Gaz)
  if (formData.chaudiere.energie === 'fioul' || formData.chaudiere.energie === 'gaz') {
    if (formData.chaudiere.photoPlaqueSignaletique && formData.chaudiere.photoPlaqueSignaletique.length > 0) {
      checkNeedNewPage(60);
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Photo plaque signalétique:', margin, currentY);
      currentY += 8;
      for (const photo of formData.chaudiere.photoPlaqueSignaletique) {
        await addPhoto(photo);
      }
      currentY += 3;
    }
    if (formData.chaudiere.photoChaudiereHaut && formData.chaudiere.photoChaudiereHaut.length > 0) {
      checkNeedNewPage(60);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Photo chaudière en haut:', margin, currentY);
      currentY += 8;
      for (const photo of formData.chaudiere.photoChaudiereHaut) {
        await addPhoto(photo);
      }
      currentY += 3;
    }
    if (formData.chaudiere.photoChaudiereBas && formData.chaudiere.photoChaudiereBas.length > 0) {
      checkNeedNewPage(60);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Photo chaudière en bas:', margin, currentY);
      currentY += 8;
      for (const photo of formData.chaudiere.photoChaudiereBas) {
        await addPhoto(photo);
      }
      currentY += 3;
    }
  }

  // Photos échangeur (Urbain)
  if (formData.chaudiere.energie === 'urbain') {
    if (formData.chaudiere.photoEchangeurPlaque && formData.chaudiere.photoEchangeurPlaque.length > 0) {
      checkNeedNewPage(60);
      doc.setFontSize(11);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'bold');
      doc.text('Photo plaque signalétique échangeur:', margin, currentY);
      currentY += 8;
      for (const photo of formData.chaudiere.photoEchangeurPlaque) {
        await addPhoto(photo);
      }
      currentY += 3;
    }
    if (formData.chaudiere.photoEchangeurHaut && formData.chaudiere.photoEchangeurHaut.length > 0) {
      checkNeedNewPage(60);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Photo échangeur en haut:', margin, currentY);
      currentY += 8;
      for (const photo of formData.chaudiere.photoEchangeurHaut) {
        await addPhoto(photo);
      }
      currentY += 3;
    }
    if (formData.chaudiere.photoEchangeurBas && formData.chaudiere.photoEchangeurBas.length > 0) {
      checkNeedNewPage(60);
      doc.setFontSize(11);
      doc.setFont('helvetica', 'bold');
      doc.text('Photo échangeur en bas:', margin, currentY);
      currentY += 8;
      for (const photo of formData.chaudiere.photoEchangeurBas) {
        await addPhoto(photo);
      }
      currentY += 3;
    }
  }

  // Photos chaudière génériques
  if (formData.chaudiere.photos && formData.chaudiere.photos.length > 0) {
    checkNeedNewPage(60);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Autres photos chaudière:', margin, currentY);
    currentY += 8;
    for (const photo of formData.chaudiere.photos) {
      await addPhoto(photo);
    }
    currentY += 3;
  }

  // Section Circuits
  checkNeedNewPage(50);
  doc.setFontSize(14);
  doc.setTextColor(3, 76, 140);
  doc.setFont('helvetica', 'bold');
  doc.text('CIRCUITS DE CHAUFFAGE', margin, currentY);
  currentY += 8;

  for (const circuit of formData.circuits) {
    checkNeedNewPage(40);

    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(3, 76, 140);
    doc.text(circuit.nom, margin, currentY);
    currentY += 6;

    const circuitInfo = [
      ['Vanne', equipements.vannes.find(v => v.id === circuit.vanne)?.nom || '-'],
      ['Pompe', equipements.pompes.find(p => p.id === circuit.pompe)?.nom || '-'],
      circuit.autre && ['Autre équipement', circuit.autre]
    ].filter(Boolean);

    doc.autoTable({
      startY: currentY,
      body: circuitInfo,
      theme: 'grid',
      headStyles: { fillColor: [3, 76, 140] },
      margin: { left: margin, right: margin, top: 30 },
      styles: { fontSize: 9 },
      didDrawPage: () => { addLogoToPage(); }
    });

    currentY = doc.lastAutoTable.finalY + 8;

    if (circuit.photoVanne && circuit.photoVanne.length > 0) {
      checkNeedNewPage(55);
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'italic');
      doc.text(`Photo vanne - ${circuit.nom}:`, margin, currentY);
      currentY += 6;
      for (const photo of circuit.photoVanne) {
        await addPhoto(photo);
      }
      currentY += 3;
    }

    if (circuit.photoPompe && circuit.photoPompe.length > 0) {
      checkNeedNewPage(55);
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'italic');
      doc.text(`Photo pompe - ${circuit.nom}:`, margin, currentY);
      currentY += 6;
      for (const photo of circuit.photoPompe) {
        await addPhoto(photo);
      }
      currentY += 3;
    }

    if (circuit.photos && circuit.photos.length > 0) {
      checkNeedNewPage(55);
      doc.setFontSize(10);
      doc.setTextColor(0, 0, 0);
      doc.setFont('helvetica', 'italic');
      doc.text(`Autres photos - ${circuit.nom}:`, margin, currentY);
      currentY += 6;
      for (const photo of circuit.photos) {
        await addPhoto(photo);
      }
      currentY += 3;
    }
  }

  // Section ECS
  checkNeedNewPage(50);
  doc.setFontSize(14);
  doc.setTextColor(3, 76, 140);
  doc.setFont('helvetica', 'bold');
  doc.text('ECS - EAU CHAUDE SANITAIRE', margin, currentY);
  currentY += 8;

  const ecsInfo = [
    ['Préparateur ECS', formData.ecs.preparateur ? 'Oui' : 'Non'],
    formData.ecs.observationsPreparateur && ['Observations préparateur', formData.ecs.observationsPreparateur],
    formData.ecs.typeEcs && ['Type de système', equipements.typesEcs.find(t => t.id === formData.ecs.typeEcs)?.nom],
    formData.ecs.pompePrimaire && ['Pompe primaire', equipements.pompes.find(p => p.id === formData.ecs.pompePrimaire)?.nom],
    formData.ecs.pompeRecyclage && ['Pompe de recyclage', equipements.pompes.find(p => p.id === formData.ecs.pompeRecyclage)?.nom],
    formData.ecs.observations && ['Observations générales', formData.ecs.observations]
  ].filter(Boolean);

  if (ecsInfo.length > 0) {
    doc.autoTable({
      startY: currentY,
      body: ecsInfo,
      theme: 'grid',
      headStyles: { fillColor: [3, 76, 140] },
      margin: { left: margin, right: margin, top: 30 },
      styles: { fontSize: 9 },
      didDrawPage: () => { addLogoToPage(); }
    });
    currentY = doc.lastAutoTable.finalY + 10;
  }

  if (formData.ecs.photoSystemeEcs && formData.ecs.photoSystemeEcs.length > 0) {
    checkNeedNewPage(60);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Photo système ECS:', margin, currentY);
    currentY += 8;
    for (const photo of formData.ecs.photoSystemeEcs) {
      await addPhoto(photo);
    }
    currentY += 3;
  }

  if (formData.ecs.photoPompePrimaire && formData.ecs.photoPompePrimaire.length > 0) {
    checkNeedNewPage(60);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Photo pompe primaire ECS:', margin, currentY);
    currentY += 8;
    for (const photo of formData.ecs.photoPompePrimaire) {
      await addPhoto(photo);
    }
    currentY += 3;
  }

  if (formData.ecs.photoPompeRecyclage && formData.ecs.photoPompeRecyclage.length > 0) {
    checkNeedNewPage(60);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Photo pompe recyclage ECS:', margin, currentY);
    currentY += 8;
    for (const photo of formData.ecs.photoPompeRecyclage) {
      await addPhoto(photo);
    }
    currentY += 3;
  }

  if (formData.ecs.photos && formData.ecs.photos.length > 0) {
    checkNeedNewPage(60);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Autres photos ECS:', margin, currentY);
    currentY += 8;
    for (const photo of formData.ecs.photos) {
      await addPhoto(photo);
    }
    currentY += 3;
  }

  // Section Armoire Électrique
  checkNeedNewPage(50);
  doc.setFontSize(14);
  doc.setTextColor(3, 76, 140);
  doc.setFont('helvetica', 'bold');
  doc.text('ARMOIRE ÉLECTRIQUE', margin, currentY);
  currentY += 8;

  if (formData.armoireElectriquePhotos && formData.armoireElectriquePhotos.length > 0) {
    checkNeedNewPage(60);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Photos armoire électrique (avant):', margin, currentY);
    currentY += 8;
    for (const photo of formData.armoireElectriquePhotos) {
      await addPhoto(photo);
    }
    currentY += 3;
  }

  if (formData.armoireElectriqueInterieurPhotos && formData.armoireElectriqueInterieurPhotos.length > 0) {
    checkNeedNewPage(60);
    doc.setFontSize(11);
    doc.setTextColor(0, 0, 0);
    doc.setFont('helvetica', 'bold');
    doc.text('Photos armoire électrique (intérieur):', margin, currentY);
    currentY += 8;
    for (const photo of formData.armoireElectriqueInterieurPhotos) {
      await addPhoto(photo);
    }
    currentY += 3;
  }

  // Section Compteurs
  if (showCompteurGaz || showCompteurEau || showCompteurEnergie || showCompteurCpcu) {
    checkNeedNewPage(50);
    doc.setFontSize(14);
    doc.setTextColor(3, 76, 140);
    doc.setFont('helvetica', 'bold');
    doc.text('COMPTEURS', margin, currentY);
    currentY += 8;

    const cableRelierLabel = (val) => val === 'oui' ? 'Oui' : val === 'non' ? 'Non' : 'Je ne sais pas';

    const cptSections = [
      { show: showCompteurGaz, label: 'Compteur Gaz', data: [
        formData.compteurs.gaz.localisation && ['Localisation', formData.compteurs.gaz.localisation === 'interieur' ? 'Intérieur' : 'Extérieur'],
        formData.compteurs.gaz.distance     && ['Distance chaufferie', formData.compteurs.gaz.distance + ' m'],
        formData.compteurs.gaz.cableRelier  && ['Liaison cable chaufferie/compteur', cableRelierLabel(formData.compteurs.gaz.cableRelier)],
        formData.compteurs.gaz.priseDisponible && ['Prise disponible', formData.compteurs.gaz.priseDisponible === 'oui' ? 'Oui' : 'Non'],
        formData.compteurs.gaz.dejaConnecte && ['Déjà connecté', formData.compteurs.gaz.dejaConnecte === 'oui' ? 'Oui' : 'Non'],
      ].filter(Boolean), photos: formData.compteurs.gaz.photos },
      { show: showCompteurCpcu, label: 'Compteur CPCU', data: [
        formData.compteurs.cpcu.distance    && ['Distance chaufferie', formData.compteurs.cpcu.distance + ' m'],
        formData.compteurs.cpcu.cableRelier && ['Liaison cable chaufferie/compteur', cableRelierLabel(formData.compteurs.cpcu.cableRelier)],
        formData.compteurs.cpcu.dejaConnecte && ['Déjà connecté', formData.compteurs.cpcu.dejaConnecte === 'oui' ? 'Oui' : 'Non'],
      ].filter(Boolean), photos: formData.compteurs.cpcu.photos },
      { show: showCompteurEau, label: "Compteur d'Eau", data: [
        formData.compteurs.eau.distance    && ['Distance chaufferie', formData.compteurs.eau.distance + ' m'],
        formData.compteurs.eau.cableRelier && ['Liaison cable chaufferie/compteur', cableRelierLabel(formData.compteurs.eau.cableRelier)],
        formData.compteurs.eau.dejaConnecte && ['Déjà connecté', formData.compteurs.eau.dejaConnecte === 'oui' ? 'Oui' : 'Non'],
      ].filter(Boolean), photos: formData.compteurs.eau.photos },
      { show: showCompteurEnergie, label: "Compteur d'Énergie", data: [
        formData.compteurs.energie.distance    && ['Distance chaufferie', formData.compteurs.energie.distance + ' m'],
        formData.compteurs.energie.cableRelier && ['Liaison cable chaufferie/compteur', cableRelierLabel(formData.compteurs.energie.cableRelier)],
        formData.compteurs.energie.dejaConnecte && ['Déjà connecté', formData.compteurs.energie.dejaConnecte === 'oui' ? 'Oui' : 'Non'],
      ].filter(Boolean), photos: formData.compteurs.energie.photos },
    ];

    for (const cpt of cptSections) {
      if (!cpt.show) continue;
      checkNeedNewPage(30);
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.setTextColor(3, 76, 140);
      doc.text(cpt.label, margin, currentY);
      currentY += 6;
      if (cpt.data.length > 0) {
        doc.autoTable({ startY: currentY, body: cpt.data, theme: 'grid', headStyles: { fillColor: [3, 76, 140] }, margin: { left: margin, right: margin, top: 30 }, styles: { fontSize: 9 }, didDrawPage: () => { addLogoToPage(); } });
        currentY = doc.lastAutoTable.finalY + 5;
      }
      if (cpt.photos?.length > 0) {
        checkNeedNewPage(60);
        doc.setFontSize(11); doc.setTextColor(0, 0, 0); doc.setFont('helvetica', 'bold');
        doc.text(`Photos ${cpt.label}:`, margin, currentY);
        currentY += 8;
        for (const photo of cpt.photos) {
          await addPhoto(photo);
        }
        currentY += 3;
      }
    }
  }

  // Pied de page sur chaque page (le logo est déjà ajouté pendant la génération)
  const pageCount = doc.internal.getNumberOfPages();
  for (let i = 1; i <= pageCount; i++) {
    doc.setPage(i);

    // Numéro de page au-dessus du pied de page
    doc.setFontSize(8);
    doc.setTextColor(100, 100, 100);
    doc.setFont('helvetica', 'italic');
    doc.text(`Page ${i} / ${pageCount}`, pageWidth - margin, pageHeight - pdpHeight - 5, { align: 'right' });

    // Image pied de page PDP.png avec ratio respecté
    if (pdpBase64) {
      try {
        doc.addImage(pdpBase64, 'PNG', margin, pageHeight - pdpHeight - 3, pdpWidth, pdpHeight);
      } catch (e) {
        console.log('PDP.png non disponible');
      }
    }
  }

  return { doc, dateStr, energieLibelle };
};

const exporterPDF = async () => {
  if (!validerFormulaire()) {
    setShowValidationModal(true);
    return;
  }
  try {
    const { doc, dateStr } = await genererPDF();
    const fileName = `Audit_Chaufferie_${formData.site.nomSite || 'Sans_nom'}_${dateStr.replace(/\//g, '-')}.pdf`;
    doc.save(fileName);
    alert('PDF téléchargé avec succès !');
  } catch (error) {
    console.error('Erreur génération PDF:', error);
    alert('Erreur lors de la génération du PDF. Veuillez réessayer.');
  }
};

const envoyerParEmail = async () => {
    if (!validerFormulaire()) {
    setShowValidationModal(true);
    return;
    }
    // 1. Demander l'email de destination
    const email = prompt('Entrez l\'adresse email de destination :', 'contact@bt-regulation.fr');
  
    if (!email) return;
  
    // Validation email
    if (!email.match(/^[^\s@]+@[^\s@]+\.[^\s@]+$/)) {
      alert('Adresse email invalide');
      return;
    }
  
    // 2. Afficher le loader
    const loader = document.createElement('div');
    loader.id = 'email-loader';
    loader.innerHTML = `
      <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                  background: rgba(0,0,0,0.7); display: flex; align-items: center; 
                  justify-content: center; z-index: 9999;">
        <div style="background: white; padding: 30px; border-radius: 10px; text-align: center; 
                    box-shadow: 0 4px 20px rgba(0,0,0,0.3);">
          <div style="width: 50px; height: 50px; border: 5px solid #f3f3f3; 
                      border-top: 5px solid #034C8C; border-radius: 50%; 
                      animation: spin 1s linear infinite; margin: 0 auto 20px;"></div>
          <p style="margin: 0; font-size: 16px; color: #333;">
            Génération et envoi du PDF en cours...<br>
            <small style="color: #666;">Veuillez patienter</small>
          </p>
        </div>
      </div>
      <style>
        @keyframes spin {
          0% { transform: rotate(0deg); }
          100% { transform: rotate(360deg); }
        }
      </style>
    `;
    document.body.appendChild(loader);
  
    try {
      // 3. Générer le PDF
      const { doc, energieLibelle } = await genererPDF();

      // 4. Convertir le PDF en Base64
      const pdfBase64 = doc.output('datauristring').split(',')[1];
  
      // 5. Préparer les données client
      const clientInfo = {
        nom: formData.site.nomSite || 'Audit Chaufferie',
        contact: formData.site.contactNom || '',
        telephone: formData.site.contactTel || '',
        email: formData.site.contactEmail || email,
        adresse: formData.site.adresse || '',
        codePostal: formData.site.codePostal || '',
        ville: formData.site.ville || '',
        // Informations techniques supplémentaires
        energie: energieLibelle,
        pilotage: equipements.pilotages.find(p => p.id === formData.chaudiere.typePilotage)?.nom || '',
        nbCircuits: formData.circuits.length
      };
  
      // 6. Envoyer au serveur
      const response = await fetch('/send-order.php', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          pdfBase64: pdfBase64,
          clientInfo: clientInfo,
          source: 'audit',
          emailDestination: email
        })
      });
  
      const result = await response.json();
  
      // 7. Supprimer le loader
      document.body.removeChild(loader);
  
      // 8. Afficher le résultat
      if (result.success) {
        // Modale de succès
        const modal = document.createElement('div');
        modal.innerHTML = `
          <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                      background: rgba(0,0,0,0.5); display: flex; align-items: center; 
                      justify-content: center; z-index: 9999;">
            <div style="background: white; padding: 40px; border-radius: 15px; text-align: center; 
                        max-width: 500px; box-shadow: 0 10px 40px rgba(0,0,0,0.3);">
              <div style="font-size: 60px; margin-bottom: 20px;">✅</div>
              <h2 style="margin: 0 0 15px 0; color: #28a745; font-size: 24px;">
                Audit envoyé avec succès !
              </h2>
              <p style="margin: 0 0 10px 0; color: #666; font-size: 14px;">
                Votre audit de chaufferie a été envoyé à :<br>
                <strong style="color: #034C8C;">${email}</strong>
              </p>
              ${result.pdfUrl ? `
                <p style="margin: 15px 0; color: #666; font-size: 14px;">
                  <a href="${result.pdfUrl}" target="_blank" 
                     style="color: #034C8C; text-decoration: none; font-weight: bold;">
                    Télécharger le PDF
                  </a>
                </p>
              ` : ''}
              <button onclick="this.closest('div').parentElement.remove()" 
                      style="margin-top: 20px; padding: 12px 30px; background: linear-gradient(135deg, #28a745 0%, #1e7e34 100%); 
                             color: white; border: none; border-radius: 8px; cursor: pointer; 
                             font-size: 16px; font-weight: bold; box-shadow: 0 4px 15px rgba(40,167,69,0.3);">
                Fermer
              </button>
            </div>
          </div>
        `;
        document.body.appendChild(modal);
      } else {
        throw new Error(result.error || 'Erreur lors de l\'envoi');
      }
  
    } catch (error) {
      // Supprimer le loader en cas d'erreur
      const loader = document.getElementById('email-loader');
      if (loader) {
        document.body.removeChild(loader);
      }
  
      // Modale d'erreur
      const errorModal = document.createElement('div');
      errorModal.innerHTML = `
        <div style="position: fixed; top: 0; left: 0; width: 100%; height: 100%; 
                    background: rgba(0,0,0,0.5); display: flex; align-items: center; 
                    justify-content: center; z-index: 9999;">
          <div style="background: white; padding: 40px; border-radius: 15px; text-align: center; 
                      max-width: 500px; box-shadow: 0 10px 40px rgba(0,0,0,0.3);">
            <div style="font-size: 60px; margin-bottom: 20px;">❌</div>
            <h2 style="margin: 0 0 15px 0; color: #dc3545; font-size: 24px;">
              Erreur d'envoi
            </h2>
            <p style="margin: 0 0 20px 0; color: #666; font-size: 14px;">
              ${error.message || 'Une erreur est survenue lors de l\'envoi de l\'audit.'}
            </p>
            <p style="margin: 0 0 20px 0; color: #666; font-size: 13px;">
              Veuillez réessayer ou télécharger le PDF pour l'envoyer manuellement.
            </p>
            <button onclick="this.closest('div').parentElement.remove()" 
                    style="padding: 12px 30px; background: linear-gradient(135deg, #dc3545 0%, #c82333 100%); 
                           color: white; border: none; border-radius: 8px; cursor: pointer; 
                           font-size: 16px; font-weight: bold; box-shadow: 0 4px 15px rgba(220,53,69,0.3);">
              Fermer
            </button>
          </div>
        </div>
      `;
      document.body.appendChild(errorModal);
  
      console.error('Erreur envoi email:', error);
    }
  };

  const nouveauAudit = () => {
    if (window.confirm('Voulez-vous vraiment créer un nouvel audit ? Les données actuelles seront effacées.')) {
      localStorage.removeItem('audit_chaufferie_draft');
      window.location.reload();
    }
  };

  return (
    <div className="audit-container-vertical">
      {showValidationModal && (
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        background: 'rgba(0,0,0,0.7)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        zIndex: 10000
      }}>
        <div style={{
          background: 'white',
          padding: '30px',
          borderRadius: '15px',
          maxWidth: '600px',
          maxHeight: '80vh',
          overflow: 'auto',
          boxShadow: '0 10px 40px rgba(0,0,0,0.3)'
        }}>
          <div style={{ fontSize: '60px', textAlign: 'center', marginBottom: '20px' }}>⚠️</div>
          <h2 style={{ margin: '0 0 20px 0', color: '#dc3545', fontSize: '24px', textAlign: 'center' }}>
            Éléments obligatoires manquantes
          </h2>
          <p style={{ marginBottom: '20px', color: '#666', textAlign: 'center' }}>
            Veuillez ajouter toutes les éléments obligatoires avant de générer ou envoyer le PDF :
          </p>

          {/* Afficher les erreurs par section */}
          <div style={{ textAlign: 'left' }}>
            {Object.entries(validationErrors).length > 0 && (
              <ul style={{ color: '#dc3545', paddingLeft: '20px' }}>
                {Object.values(validationErrors).map((error, index) => (
                  <li key={index} style={{ marginBottom: '8px' }}>{error}</li>
                ))}
              </ul>
            )}
          </div>

          <button
            onClick={() => setShowValidationModal(false)}
            style={{
              marginTop: '20px',
              padding: '12px 30px',
              background: 'linear-gradient(135deg, #034C8C 0%, #062540 100%)',
              color: 'white',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              fontSize: '16px',
              fontWeight: 'bold',
              width: '100%',
              boxShadow: '0 4px 15px rgba(3,76,140,0.3)'
            }}
          >
            Compris, je vais ajouter les photos
          </button>
        </div>
      </div>
    )}
      <header className="audit-header">
        <div className="audit-header-content">
          <div className="audit-logo-section">
            <img
              src="images/Logo1.png"
              alt="VIGILIA BT Régulation"
              className="audit-logo"
              onError={(e) => e.target.style.display = 'none'}
            />
            <div className="audit-logo-text">
              <h1 className="audit-title">Relevé de votre Chaufferie</h1>
              <p className="audit-subtitle">Formulaire de relevé - VIGILIA BT Régulation</p>
            </div>
          </div>
          {!isOnline && (
            <div className="audit-offline-indicator">
              <span>📴</span>
              <span>Hors ligne</span>
            </div>
          )}
          <button
            className="audit-btn bt-btn-outline"
            onClick={() => navigate('/')}
          >
            ← Retour
          </button>
        </div>
      </header>

          
      {/* MINI NAVBAR DE NAVIGATION */}
      <div className="audit-mini-navbar">
        <div className="audit-mini-navbar-content">
          <div className="audit-mini-navbar-sections">
            <button
              className={`audit-mini-navbar-btn ${activeSection === 'site' ? 'active' : ''}`}
              onClick={() => scrollToSection('site')}
            >
              <span className="section-number">1</span>
              <span className="section-name">Site</span>
            </button>
            
            <button
              className={`audit-mini-navbar-btn ${activeSection === 'chaudiere' ? 'active' : ''}`}
              onClick={() => scrollToSection('chaudiere')}
            >
              <span className="section-number">2</span>
              <span className="section-name">Chaudière</span>
            </button>
            
            <button
              className={`audit-mini-navbar-btn ${activeSection === 'circuits' ? 'active' : ''}`}
              onClick={() => scrollToSection('circuits')}
            >
              <span className="section-number">3</span>
              <span className="section-name">Circuits</span>
            </button>
            
            <button
              className={`audit-mini-navbar-btn ${activeSection === 'ecs' ? 'active' : ''}`}
              onClick={() => scrollToSection('ecs')}
            >
              <span className="section-number">4</span>
              <span className="section-name">ECS</span>
            </button>
            
            <button
              className={`audit-mini-navbar-btn ${activeSection === 'armoire' ? 'active' : ''}`}
              onClick={() => scrollToSection('armoire')}
            >
              <span className="section-number">5</span>
              <span className="section-name">Armoire</span>
            </button>
            <button
              className={`audit-mini-navbar-btn ${activeSection === 'compteurs' ? 'active' : ''}`}
              onClick={() => scrollToSection('compteurs')}
            >
              <span className="section-number">6</span>
              <span className="section-name">Compteurs</span>
            </button>
          </div>
          
          <button
            className="audit-mini-navbar-reset"
            onClick={reinitialiserFormulaire}
          >
            <span>Réinitialiser</span>
          </button>
        </div>
        
        {/* Barre de progression */}
        <div 
          className="audit-mini-navbar-progress" 
          style={{ width: `${scrollProgress}%` }}
        />
      </div>


      <div className="audit-main-content">
        <div className="audit-form-wrapper">
          
          {/* SECTION 1 - SITE */}
          <section className="audit-section" id="section-site" ref={sectionRefs.site}>
            <div className="audit-section-header-row">
              <h2 className="audit-section-title">1. Informations du Site</h2>
              <button
                type="button"
                className="audit-btn-geoloc"
                onClick={remplirPositionActuelle}
                disabled={isLocating}
                title="Remplir automatiquement l'adresse selon votre position GPS"
              >
                {isLocating ? (
                  <><span className="audit-geoloc-spinner" /> Localisation...</>
                ) : (
                  <>📍 Ma position</>
                )}
              </button>
            </div>

            <div className="audit-row">
              <div className="audit-form-group">
                <label className="audit-label">Nom du site *</label>
                <input
                  type="text"
                  className="audit-input"
                  value={formData.site.nomSite}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    site: { ...prev.site, nomSite: e.target.value }
                  }))}
                  placeholder="Ex: Résidence Les Lilas"
                />
              </div>

              <div className="audit-form-group">
                <label className="audit-label">Ville *</label>
                <input
                  type="text"
                  className="audit-input"
                  value={formData.site.ville}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    site: { ...prev.site, ville: e.target.value }
                  }))}
                  placeholder="Ex: Paris"
                />
              </div>
            </div>

            <div className="audit-form-group">
              <label className="audit-label">Adresse</label>
              <input
                type="text"
                className="audit-input"
                value={formData.site.adresse}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  site: { ...prev.site, adresse: e.target.value }
                }))}
                placeholder="Ex: 15 rue de la Paix"
              />
            </div>

            <div className="audit-form-group">
              <label className="audit-label">Code postal</label>
              <input
                type="text"
                className="audit-input"
                value={formData.site.codePostal}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  site: { ...prev.site, codePostal: e.target.value }
                }))}
                placeholder="Ex: 75001"
              />
            </div>

            <h3 className="audit-subsection-title">Contact sur site</h3>

            <div className="audit-row">
              <div className="audit-form-group">
                <label className="audit-label">Nom du contact</label>
                <input
                  type="text"
                  className="audit-input"
                  value={formData.site.contactNom}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    site: { ...prev.site, contactNom: e.target.value }
                  }))}
                  placeholder="Ex: M. Dupont"
                />
              </div>

              <div className="audit-form-group">
                <label className="audit-label">Téléphone *</label>
                <input
                  type="tel"
                  className="audit-input"
                  value={formData.site.contactTel}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    site: { ...prev.site, contactTel: e.target.value }
                  }))}
                  placeholder="Ex: 01 23 45 67 89"
                />
              </div>
            </div>

            <div className="audit-form-group">
              <label className="audit-label">Email *</label>
              <input
                type="email"
                className="audit-input"
                value={formData.site.contactEmail}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  site: { ...prev.site, contactEmail: e.target.value }
                }))}
                placeholder="Ex: contact@example.com"
              />
            </div>

            {/* Photo synoptique */}
            <div className="audit-form-group">
              <label className="audit-label">Photo du synoptique</label>
              <div className={`audit-photo-upload-wrapper ${isPhotoMissing('synoptique') ? 'error' : ''}`}>
                <input type="file" accept="image/*" capture="environment" id="upload-site-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('site')} />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  id="upload-site"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload('site')}
                />
                <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-site-camera', 'upload-site')}>
                  <div className="audit-photo-icon">📷</div>
                  <p>Cliquez pour ajouter des photos</p>
                  <small>Max 10MB par photo - Format JPG, PNG</small>
                </label>
                <div className="audit-photo-tooltip">
                  <span className="audit-photo-tooltip-title">Photo du synoptique</span>
                  <span className="audit-photo-tooltip-desc">
                    <img src="images/audit/hover/Synoptique.png" alt="" className='audit-photo-tooltip-img' />
                    Photographiez le schéma de principe affiché sur la porte de l'armoire électrique ou dans la chaufferie
                  </span>
                </div>
              </div>

              {formData.site.photosArmoire && formData.site.photosArmoire.length > 0 && (
                <div className="audit-photo-gallery">
                  {formData.site.photosArmoire.map(photo => (
                    <div key={photo.id} className="audit-photo-thumbnail">
                      <img src={photo.dataUrl} alt={photo.name} />
                      <button
                        className="audit-photo-delete-btn"
                        onClick={() => supprimerPhoto('site', photo.id)}
                        title="Supprimer"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* SECTION 2 - CHAUDIERE */}
          <section className="audit-section" id="section-chaudiere" ref={sectionRefs.chaudiere}>
            <h2 className="audit-section-title">2. Chaudière / Primaire</h2>

            <div className="audit-form-group">
              <label className="audit-label">Type d'énergie *</label>
              <div className="audit-gallery-compact">
                {equipements.energies.map(energie => (
                  <div
                    key={energie.id}
                    className={`audit-gallery-item-compact ${formData.chaudiere.energie === energie.id ? 'selected' : ''}`}
                    onClick={() => {
                      const prevEnergie = formData.chaudiere.energie;
                      const newEnergie = energie.id;
                      const changementType = prevEnergie === 'urbain' !== (newEnergie === 'urbain') || prevEnergie === 'autre' !== (newEnergie === 'autre');
                      setFormData(prev => ({
                        ...prev,
                        chaudiere: {
                          ...prev.chaudiere,
                          energie: newEnergie,
                          ...(changementType ? { typePilotage: '' } : {}),
                          ...(newEnergie === 'autre' ? { plusieursChaudieres: false, estCascade: false, gererCascade: false, nombreChaudieres: 1 } : {})
                        }
                      }));
                    }}
                  >
                    {formData.chaudiere.energie === energie.id && <div className="audit-check-mark">✓</div>}
                    <span className="audit-gallery-icon-compact">{energie.icon}</span>
                    <span className="audit-gallery-name-compact">{energie.nom}</span>
                  </div>
                ))}
              </div>
            </div>

            {formData.chaudiere.energie === 'autre' && (
              <div className="audit-form-group">
                <label className="audit-label">Précisez le type d'énergie *</label>
                <input
                  type="text"
                  className="audit-input"
                  value={formData.chaudiere.energieAutre}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    chaudiere: { ...prev.chaudiere, energieAutre: e.target.value }
                  }))}
                  placeholder="Ex: Biomasse, Solaire, Géothermie..."
                />
              </div>
            )}

            {/* Photos pour Fioul/Gaz */}
            {(formData.chaudiere.energie === 'fioul' || formData.chaudiere.energie === 'gaz') && (
              <>
              <div className="audit-photos-required-group-horizontal">
                {/* Photo plaque signalétique */}
                <div className="audit-form-group audit-form-group-multiple">
                  <label className="audit-label">Photo plaque signalétique</label>
                  <div className={`audit-photo-upload-wrapper ${isPhotoMissing('photoPlaqueSignaletique') ? 'error' : ''}`}>
                    <input type="file" accept="image/*" capture="environment" id="upload-plaque-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('photoPlaqueSignaletique')} />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      id="upload-plaque"
                      style={{ display: 'none' }}
                      onChange={handlePhotoUpload('photoPlaqueSignaletique')}
                    />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-plaque-camera', 'upload-plaque')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Ajouter photo</p>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Plaque signalétique</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/Plaque.png" alt="" className='audit-photo-tooltip-img' />
                        Photographiez la plaque avec la marque, modèle et puissance de la chaudière
                      </span>
                    </div>
                  </div>
                  {formData.chaudiere.photoPlaqueSignaletique && formData.chaudiere.photoPlaqueSignaletique.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.chaudiere.photoPlaqueSignaletique.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button
                            className="audit-photo-delete-btn"
                            onClick={() => supprimerPhoto('photoPlaqueSignaletique', photo.id)}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Photo chaudière */}
                <div className="audit-form-group audit-form-group-multiple">
                  <label className="audit-label">Photo chaudière</label>
                  <div className={`audit-photo-upload-wrapper ${isPhotoMissing('photoChaudiereHaut') ? 'error' : ''}`}>
                    <input type="file" accept="image/*" capture="environment" id="upload-haut-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('photoChaudiereHaut')} />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      id="upload-haut"
                      style={{ display: 'none' }}
                      onChange={handlePhotoUpload('photoChaudiereHaut')}
                    />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-haut-camera', 'upload-haut')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Ajouter photo</p>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Photo chaudière</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/CHD.png" alt="" className='audit-photo-tooltip-img' />
                        Photographiez la chaudière (vue d'ensemble, raccordements, brûleur et tableau de commande)
                      </span>
                    </div>
                  </div>
                  {formData.chaudiere.photoChaudiereHaut && formData.chaudiere.photoChaudiereHaut.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.chaudiere.photoChaudiereHaut.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button
                            className="audit-photo-delete-btn"
                            onClick={() => supprimerPhoto('photoChaudiereHaut', photo.id)}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>

                <div className="audit-form-group audit-optional-section">
                  <h3 
                    className="audit-subsection-title" 
                    onClick={() => setOptionalSectionOpen(!optionalSectionOpen)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Optionnel {optionalSectionOpen ? '▼' : '►'}
                  </h3>
                  
                  {optionalSectionOpen && (
                    <>
                      <label className="audit-label">Type de pilotage (si connu)</label>
                      <div className="audit-radio-group-vertical">
                        {equipements.pilotages.map(pilotage => (
                          <label key={pilotage.id} className="audit-radio-label-vertical">
                            <input
                              type="radio"
                              name="typePilotage"
                              checked={formData.chaudiere.typePilotage === pilotage.id}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                chaudiere: { ...prev.chaudiere, typePilotage: pilotage.id }
                              }))}
                            />
                            <span className="audit-radio-text">
                              {pilotage.nom} <span className="audit-radio-description">({pilotage.description})</span>
                            </span>
                          </label>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              </>
            )}

            {/* Photos pour Urbain */}
            {formData.chaudiere.energie === 'urbain' && (
              <>
              <div className="audit-photos-required-group-horizontal">
                {/* Photo plaque échangeur */}
                <div className="audit-form-group">
                  <label className="audit-label">Photo plaque signalétique</label>
                  <div className={`audit-photo-upload-wrapper ${isPhotoMissing('photoEchangeurPlaque') ? 'error' : ''}`}>
                    <input type="file" accept="image/*" capture="environment" id="upload-echangeur-plaque-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('photoEchangeurPlaque')} />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      id="upload-echangeur-plaque"
                      style={{ display: 'none' }}
                      onChange={handlePhotoUpload('photoEchangeurPlaque')}
                    />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-echangeur-plaque-camera', 'upload-echangeur-plaque')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Ajouter photo</p>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Plaque échangeur</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/PLAK_ECH.jpg" alt="" className='audit-photo-tooltip-img' />
                        Photographiez la plaque avec les caractéristiques de l'échangeur urbain
                      </span>
                    </div>
                  </div>
                  {formData.chaudiere.photoEchangeurPlaque && formData.chaudiere.photoEchangeurPlaque.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.chaudiere.photoEchangeurPlaque.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button
                            className="audit-photo-delete-btn"
                            onClick={() => supprimerPhoto('photoEchangeurPlaque', photo.id)}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Photo échangeur haut */}
                <div className="audit-form-group">
                  <label className="audit-label">Photo échangeur en haut</label>
                  <div className={`audit-photo-upload-wrapper ${isPhotoMissing('photoEchangeurHaut') ? 'error' : ''}`}>
                    <input type="file" accept="image/*" capture="environment" id="upload-echangeur-haut-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('photoEchangeurHaut')} />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      id="upload-echangeur-haut"
                      style={{ display: 'none' }}
                      onChange={handlePhotoUpload('photoEchangeurHaut')}
                    />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-echangeur-haut-camera', 'upload-echangeur-haut')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Ajouter photo</p>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Échangeur haut</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/ECH_HAUT.png" alt="" className="audit-photo-tooltip-img" />
                        Photographiez la partie supérieure avec les raccordement de l'arrivée vapeur et départ secondaire
                      </span>
                    </div>
                  </div>
                  {formData.chaudiere.photoEchangeurHaut && formData.chaudiere.photoEchangeurHaut.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.chaudiere.photoEchangeurHaut.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button
                            className="audit-photo-delete-btn"
                            onClick={() => supprimerPhoto('photoEchangeurHaut', photo.id)}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Photo échangeur bas */}
                <div className="audit-form-group">
                  <label className="audit-label">Photo échangeur en bas</label>
                  <div className={`audit-photo-upload-wrapper ${isPhotoMissing('photoEchangeurBas') ? 'error' : ''}`}>
                    <input type="file" accept="image/*" capture="environment" id="upload-echangeur-bas-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('photoEchangeurBas')} />
                    <input
                      type="file"
                      accept="image/*"
                      multiple
                      id="upload-echangeur-bas"
                      style={{ display: 'none' }}
                      onChange={handlePhotoUpload('photoEchangeurBas')}
                    />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-echangeur-bas-camera', 'upload-echangeur-bas')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Ajouter photo</p>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Échangeur bas</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/ECH_BAS.png" alt="" className="audit-photo-tooltip-img" />
                        Photographiez la partie inférieure et le système de régulation avec le retour secondaire et la sortie condensat
                      </span>
                    </div>
                  </div>
                  {formData.chaudiere.photoEchangeurBas && formData.chaudiere.photoEchangeurBas.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.chaudiere.photoEchangeurBas.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button
                            className="audit-photo-delete-btn"
                            onClick={() => supprimerPhoto('photoEchangeurBas', photo.id)}
                          >✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="audit-form-group audit-optional-section">
                <h3 
                  className="audit-subsection-title" 
                  onClick={() => setOptionalSectionOpen(!optionalSectionOpen)}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                Optionnel {optionalSectionOpen ? '▼' : '►'}
                </h3>
                  
                {optionalSectionOpen && (
                  <>
                <div className="audit-form-group">
                  <label className="audit-label">Pilotage Échangeur (si connu)</label>
                  <div className="audit-radio-group-vertical">
                    {equipements.pilotagesEchangeur.map(pilotage => (
                      <label key={pilotage.id} className="audit-radio-label-vertical">
                        <input
                          type="radio"
                          name="typePilotageEchangeur"
                          checked={formData.chaudiere.typePilotage === pilotage.id}
                          onChange={() => setFormData(prev => ({
                            ...prev,
                            chaudiere: { ...prev.chaudiere, typePilotage: pilotage.id }
                          }))}
                        />
                        <span className="audit-radio-text">{pilotage.nom}</span>
                      </label>
                    ))}
                  </div>
                </div>
                </>
                )}
              </div>
              </>
            )}
            {formData.chaudiere.energie !== 'autre' && (
              <>
                <div className="audit-form-group">
                  <label className="audit-label">
                    {formData.chaudiere.energie === 'urbain' ? 'Plusieurs Échangeurs ?' : 'Plusieurs chaudières ?'}
                  </label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input
                        type="radio"
                        name="plusieursChaudieres"
                        checked={formData.chaudiere.plusieursChaudieres === true}
                        onChange={() => setFormData(prev => ({
                          ...prev,
                          chaudiere: { ...prev.chaudiere, plusieursChaudieres: true }
                        }))}
                      />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input
                        type="radio"
                        name="plusieursChaudieres"
                        checked={formData.chaudiere.plusieursChaudieres === false}
                        onChange={() => setFormData(prev => ({
                          ...prev,
                          chaudiere: {
                            ...prev.chaudiere,
                            plusieursChaudieres: false,
                            estCascade: false,
                            gererCascade: false,
                            nombreChaudieres: 1
                          }
                        }))}
                      />
                      Non
                    </label>
                  </div>
                </div>

                {formData.chaudiere.plusieursChaudieres && (
                  <>
                    <div className="audit-form-group">
                      <label className="audit-label">
                        {formData.chaudiere.energie === 'urbain' ? 'Nombre d\'échangeurs' : 'Nombre de chaudières'}
                      </label>
                      <input
                        type="number"
                        className="audit-input"
                        min="2"
                        style={{ maxWidth: '150px' }}
                        value={formData.chaudiere.nombreChaudieres}
                        onChange={(e) => setFormData(prev => ({
                          ...prev,
                          chaudiere: { ...prev.chaudiere, nombreChaudieres: parseInt(e.target.value) || 2 }
                        }))}
                      />
                    </div>

                    <div className="audit-form-group">
                      <label className="audit-label">S'agit-il d'une cascade ?</label>
                      <div className="audit-radio-group">
                        <label className="audit-radio-label">
                          <input
                            type="radio"
                            name="estCascade"
                            checked={formData.chaudiere.estCascade === true}
                            onChange={() => setFormData(prev => ({
                              ...prev,
                              chaudiere: { ...prev.chaudiere, estCascade: true }
                            }))}
                          />
                          Oui
                        </label>
                        <label className="audit-radio-label">
                          <input
                            type="radio"
                            name="estCascade"
                            checked={formData.chaudiere.estCascade === false}
                            onChange={() => setFormData(prev => ({
                              ...prev,
                              chaudiere: { ...prev.chaudiere, estCascade: false, gererCascade: false }
                            }))}
                          />
                          Non
                        </label>
                      </div>
                    </div>

                    {formData.chaudiere.estCascade && (
                      <div className="audit-form-group">
                        <label className="audit-label">Doit-on la gérer ?</label>
                        <div className="audit-radio-group">
                          <label className="audit-radio-label">
                            <input
                              type="radio"
                              name="gererCascade"
                              checked={formData.chaudiere.gererCascade === true}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                chaudiere: { ...prev.chaudiere, gererCascade: true }
                              }))}
                            />
                            Oui
                          </label>
                          <label className="audit-radio-label">
                            <input
                              type="radio"
                              name="gererCascade"
                              checked={formData.chaudiere.gererCascade === false}
                              onChange={() => setFormData(prev => ({
                                ...prev,
                                chaudiere: { ...prev.chaudiere, gererCascade: false }
                              }))}
                            />
                            Non
                          </label>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </>
            )}

            <div className="audit-form-group">
              <label className="audit-label">Observations</label>
              <textarea
                className="audit-textarea"
                value={formData.chaudiere.observations}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  chaudiere: { ...prev.chaudiere, observations: e.target.value }
                }))}
                placeholder="Notez ici toute information complémentaire..."
              />
            </div>

            <div className="audit-form-group">
              <label className="audit-label">Autres photos de la chaudière</label>
              <input type="file" accept="image/*" capture="environment" id="upload-chaudiere-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('chaudiere')} />
              <input
                type="file"
                accept="image/*"
                multiple
                id="upload-chaudiere"
                style={{ display: 'none' }}
                onChange={handlePhotoUpload('chaudiere')}
              />
              <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-chaudiere-camera', 'upload-chaudiere')}>
                <div className="audit-photo-icon">📷</div>
                <p>Cliquez pour ajouter des photos</p>
                <small>Max 10MB par photo - Format JPG, PNG</small>
              </label>

              {formData.chaudiere.photos && formData.chaudiere.photos.length > 0 && (
                <div className="audit-photo-gallery">
                  {formData.chaudiere.photos.map(photo => (
                    <div key={photo.id} className="audit-photo-thumbnail">
                      <img src={photo.dataUrl} alt={photo.name} />
                      <button
                        className="audit-photo-delete-btn"
                        onClick={() => supprimerPhoto('chaudiere', photo.id)}
                        title="Supprimer"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </section>

          {/* SECTION 3 - CIRCUITS */}
          <section className="audit-section" id="section-circuits" ref={sectionRefs.circuits}>
            <h2 className="audit-section-title">3. Circuits de chauffage</h2>

            <div className="audit-form-group">
              <label className="audit-label">Combien de réseaux de chauffage ?</label>
              <input
                type="number"
                className="audit-input"
                min="1"
                value={formData.circuits.length}
                onChange={(e) => ajusterNombreCircuits(parseInt(e.target.value) || 1)}
                style={{ maxWidth: '150px' }}
              />
            </div>

            {formData.circuits.map((circuit, index) => (
              <div key={circuit.id} className="audit-circuit-card-compact">
                <div className="audit-circuit-header-compact">
                  <input
                    type="text"
                    className="audit-circuit-name-input-compact"
                    value={circuit.nom}
                    onChange={(e) => renommerCircuit(circuit.id, e.target.value)}
                    placeholder="Nom du circuit"
                  />
                  {formData.circuits.length > 1 && (
                    <button
                      className="audit-btn-icon-compact audit-btn-danger"
                      onClick={() => supprimerCircuit(circuit.id)}
                      title="Supprimer"
                    >✕</button>
                  )}
                </div>

                {/* Photos Vanne / Pompe */}
                <div className="audit-photos-required-group-horizontal">
                  {/* Photo Vanne */}
                  <div className="audit-form-group audit-form-group-multiple">
                    <label className="audit-label">Photo vanne</label>
                    <div className={`audit-photo-upload-wrapper ${isPhotoMissing('circuitVanne', circuit.id) ? 'error' : ''}`}>
                      <input type="file" accept="image/*" capture="environment" id={`upload-vanne-${circuit.id}-camera`} style={{ display: 'none' }} onChange={handlePhotoUpload('circuitVanne', circuit.id)} />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        id={`upload-vanne-${circuit.id}`}
                        style={{ display: 'none' }}
                        onChange={handlePhotoUpload('circuitVanne', circuit.id)}
                      />
                      <label className="audit-photo-upload" onClick={() => handlePhotoClick(`upload-vanne-${circuit.id}-camera`, `upload-vanne-${circuit.id}`)}>
                        <div className="audit-photo-icon">📷</div>
                        <p>Ajouter photo</p>
                      </label>
                      <div className="audit-photo-tooltip">
                        <span className="audit-photo-tooltip-title">Vanne du circuit</span>
                        <span className="audit-photo-tooltip-desc">
                          {/* ——— NOUVEAU : info générale sur la vanne ——— */}
                          <img src="images/audit/hover/V3V-info.png" alt="" className='audit-photo-tooltip-img' />
                          Photographiez la vanne de régulation avec son moteur et ses raccordements
                        </span>
                      </div>
                    </div>
                    {circuit.photoVanne && circuit.photoVanne.length > 0 && (
                      <div className="audit-photo-gallery">
                        {circuit.photoVanne.map(photo => (
                          <div key={photo.id} className="audit-photo-thumbnail">
                            <img src={photo.dataUrl} alt={photo.name} />
                            <button
                              className="audit-photo-delete-btn"
                              onClick={() => supprimerPhoto('circuitVanne', photo.id, circuit.id)}
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Photo Pompe */}
                  <div className="audit-form-group audit-form-group-multiple">
                    <label className="audit-label">Photo pompe</label>
                    <div className={`audit-photo-upload-wrapper ${isPhotoMissing('circuitPompe', circuit.id) ? 'error' : ''}`}>
                      <input type="file" accept="image/*" capture="environment" id={`upload-pompe-${circuit.id}-camera`} style={{ display: 'none' }} onChange={handlePhotoUpload('circuitPompe', circuit.id)} />
                      <input
                        type="file"
                        accept="image/*"
                        multiple
                        id={`upload-pompe-${circuit.id}`}
                        style={{ display: 'none' }}
                        onChange={handlePhotoUpload('circuitPompe', circuit.id)}
                      />
                      <label className="audit-photo-upload" onClick={() => handlePhotoClick(`upload-pompe-${circuit.id}-camera`, `upload-pompe-${circuit.id}`)}>
                        <div className="audit-photo-icon">📷</div>
                        <p>Ajouter photo</p>
                      </label>
                      <div className="audit-photo-tooltip">
                        <span className="audit-photo-tooltip-title">Pompe du circuit</span>
                        <span className="audit-photo-tooltip-desc">
                          {/* ——— NOUVEAU : pompe simple ——— */}
                          <img src="images/audit/hover/Pompe-double-simple-INFO.png" alt="" className='audit-photo-tooltip-img' />
                          Photographiez la pompe du réseau de chauffage, en détail et une vue d'ensemble
                        </span>
                      </div>
                    </div>
                    {circuit.photoPompe && circuit.photoPompe.length > 0 && (
                      <div className="audit-photo-gallery">
                        {circuit.photoPompe.map(photo => (
                          <div key={photo.id} className="audit-photo-thumbnail">
                            <img src={photo.dataUrl} alt={photo.name} />
                            <button
                              className="audit-photo-delete-btn"
                              onClick={() => supprimerPhoto('circuitPompe', photo.id, circuit.id)}
                            >✕</button>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                {/* Section optionnelle circuits */}
                <div className="audit-form-group audit-optional-section">
                  <h3 
                    className="audit-subsection-title" 
                    onClick={() => setOptionalSectionOpen(!optionalSectionOpen)}
                    style={{ cursor: 'pointer', userSelect: 'none' }}
                  >
                    Optionnel {optionalSectionOpen ? '▼' : '►'}
                  </h3>
                  
                  {optionalSectionOpen && (
                    <>
                      <div className="audit-label-with-info">
                        <label className="audit-label">Type de vanne (si connu)</label>
                        <div
                          className="audit-info-wrapper"
                          onMouseEnter={() => setActiveTooltip('circuit-vanne-info')}
                          onMouseLeave={() => setActiveTooltip(null)}
                        >
                          <div className={`audit-photo-tooltip ${activeTooltip === 'circuit-vanne-info' ? 'active' : ''}`}>
                            <span className="audit-photo-tooltip-title">Identifier le type de vanne</span>
                            <span className="audit-photo-tooltip-desc">
                              <img src="images/audit/hover/V3V-ETI.png" alt="" className='audit-photo-tooltip-img' />
                              Repérez l'étiquette ou la plaque sur le corps de la vanne pour identifier son modèle (V3V, V2V...)
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="audit-radio-group-vertical">
                        {equipements.vannes.map(vanne => (
                          <label key={vanne.id} className="audit-radio-label-vertical">
                            <input
                              type="radio"
                              name={`vanne-${circuit.id}`}
                              checked={circuit.vanne === vanne.id}
                              onChange={() => {
                                const newCircuits = [...formData.circuits];
                                newCircuits[index] = { ...newCircuits[index], vanne: vanne.id };
                                setFormData(prev => ({ ...prev, circuits: newCircuits }));
                              }}
                            />
                            <span className="audit-radio-text">{vanne.nom}</span>
                          </label>
                        ))}
                      </div>

                      <div className="audit-label-with-info">
                        <label className="audit-label">Type de pompe (si connu)</label>
                        <div
                          className="audit-info-wrapper"
                          onMouseEnter={() => setActiveTooltip('circuit-pompe-info')}
                          onMouseLeave={() => setActiveTooltip(null)}
                        >
                          <div className={`audit-photo-tooltip ${activeTooltip === 'circuit-pompe-info' ? 'active' : ''}`}>
                            <span className="audit-photo-tooltip-title">Identifier le type de pompe</span>
                            <span className="audit-photo-tooltip-desc">
                              <img src="images/audit/hover/Pompe-double-ETI.png" alt="" className='audit-photo-tooltip-img' />
                              Une pompe double possède deux corps côte à côte. Repérez l'étiquette sur le corps de la pompe pour identifier le modèle.
                            </span>
                          </div>
                        </div>
                      </div>
                      <div className="audit-radio-group-vertical">
                        {equipements.pompes.map(pompe => (
                          <label key={pompe.id} className="audit-radio-label-vertical">
                            <input
                              type="radio"
                              name={`pompe-${circuit.id}`}
                              checked={circuit.pompe === pompe.id}
                              onChange={() => {
                                const newCircuits = [...formData.circuits];
                                newCircuits[index] = { ...newCircuits[index], pompe: pompe.id };
                                setFormData(prev => ({ ...prev, circuits: newCircuits }));
                              }}
                            />
                            <span className="audit-radio-text">{pompe.nom}</span>
                          </label>
                        ))}
                      </div>

                      <div className="audit-form-group">
                        <label className="audit-label">Autres équipements</label>
                        <input
                          type="text"
                          className="audit-input"
                          value={circuit.autre}
                          onChange={(e) => {
                            const newCircuits = [...formData.circuits];
                            newCircuits[index] = { ...newCircuits[index], autre: e.target.value };
                            setFormData(prev => ({ ...prev, circuits: newCircuits }));
                          }}
                          placeholder="Ex: Circulateur, vase d'expansion..."
                        />
                      </div>
                    </>
                  )}
                </div>

                <div className="audit-form-group">
                  <label className="audit-label">Autres photos du circuit</label>
                  <input type="file" accept="image/*" capture="environment" id={`upload-circuit-${circuit.id}-camera`} style={{ display: 'none' }} onChange={handlePhotoUpload('circuit', circuit.id)} />
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    id={`upload-circuit-${circuit.id}`}
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload('circuit', circuit.id)}
                  />
                  <label className="audit-photo-upload" onClick={() => handlePhotoClick(`upload-circuit-${circuit.id}-camera`, `upload-circuit-${circuit.id}`)}>
                    <div className="audit-photo-icon">📷</div>
                    <p>Cliquez pour ajouter des photos</p>
                    <small>Max 10MB par photo - Format JPG, PNG</small>
                  </label>

                  {circuit.photos && circuit.photos.length > 0 && (
                    <div className="audit-photo-gallery">
                      {circuit.photos.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button
                            className="audit-photo-delete-btn"
                            onClick={() => supprimerPhoto('circuit', photo.id, circuit.id)}
                            title="Supprimer"
                          >
                            ✕
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
            ))}

            <button
              className="audit-btn audit-btn-success"
              onClick={ajouterCircuit}
            >
              + Ajouter un circuit
            </button>
          </section>

          {/* SECTION 4 - ECS */}
          <section className="audit-section" id="section-ecs" ref={sectionRefs.ecs}>
            <h2 className="audit-section-title">4. ECS - Eau Chaude Sanitaire</h2>

            <div className="audit-form-group">
              <label className="audit-label">Préparateur ECS</label>
              <div className="audit-radio-group">
                <label className="audit-radio-label">
                  <input
                    type="radio"
                    name="preparateur-ecs"
                    checked={formData.ecs.preparateur === true}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      ecs: { ...prev.ecs, preparateur: true }
                    }))}
                  />
                  Oui
                </label>
                <label className="audit-radio-label">
                  <input
                    type="radio"
                    name="preparateur-ecs"
                    checked={formData.ecs.preparateur === false}
                    onChange={() => setFormData(prev => ({
                      ...prev,
                      ecs: { ...prev.ecs, preparateur: false }
                    }))}
                  />
                  Non
                </label>
              </div>
            </div>

            <div className="audit-form-group">
              <label className="audit-label">Observations Préparateur</label>
              <textarea
                className="audit-textarea"
                value={formData.ecs.observationsPreparateur || ''}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  ecs: { ...prev.ecs, observationsPreparateur: e.target.value }
                }))}
                placeholder="Informations sur le préparateur ECS..."
              />
            </div>

          <div className="audit-photos-required-group-horizontal">
            {/* Photo système ECS */}
            <div className="audit-form-group audit-form-group-multiple">
              <label className="audit-label">Photo système ECS</label>
              <div className={`audit-photo-upload-wrapper ${isPhotoMissing('photoSystemeEcs') ? 'error' : ''}`}>
                <input type="file" accept="image/*" capture="environment" id="upload-systeme-ecs-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('photoSystemeEcs')} />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  id="upload-systeme-ecs"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload('photoSystemeEcs')}
                />
                <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-systeme-ecs-camera', 'upload-systeme-ecs')}>
                  <div className="audit-photo-icon">📷</div>
                  <p>Ajouter photo</p>
                </label>
                <div className="audit-photo-tooltip">
                  <span className="audit-photo-tooltip-title">Système ECS</span>
                  <span className="audit-photo-tooltip-desc">
                    <img src="images/audit/hover/STK_1_INFO.png" alt="" className="audit-photo-tooltip-img" />
                    Photographiez l'ensemble du système (ballon, échangeur, raccordements)
                  </span>
                </div>
              </div>
              {formData.ecs.photoSystemeEcs && formData.ecs.photoSystemeEcs.length > 0 && (
                <div className="audit-photo-gallery">
                  {formData.ecs.photoSystemeEcs.map(photo => (
                    <div key={photo.id} className="audit-photo-thumbnail">
                      <img src={photo.dataUrl} alt={photo.name} />
                      <button
                        className="audit-photo-delete-btn"
                        onClick={() => supprimerPhoto('photoSystemeEcs', photo.id)}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Photo pompe primaire */}
            <div className="audit-form-group audit-form-group-multiple">
              <label className="audit-label">Photo pompe primaire</label>
              <div className={`audit-photo-upload-wrapper ${isPhotoMissing('photoPompePrimaire') ? 'error' : ''}`}>
                <input type="file" accept="image/*" capture="environment" id="upload-pompe-primaire-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('photoPompePrimaire')} />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  id="upload-pompe-primaire"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload('photoPompePrimaire')}
                />
                <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-pompe-primaire-camera', 'upload-pompe-primaire')}>
                  <div className="audit-photo-icon">📷</div>
                  <p>Ajouter photo</p>
                </label>
                <div className="audit-photo-tooltip">
                  <span className="audit-photo-tooltip-title">Pompe primaire ECS</span>
                  <span className="audit-photo-tooltip-desc">
                    {/* ——— NOUVEAU : pompe simple pour primaire ECS ——— */}
                    <img src="images/audit/hover/Pompe-Simple.png" alt="" className='audit-photo-tooltip-img' />
                    Photographiez la pompe primaire avec sa plaque visible
                  </span>
                </div>
              </div>
              {formData.ecs.photoPompePrimaire && formData.ecs.photoPompePrimaire.length > 0 && (
                <div className="audit-photo-gallery">
                  {formData.ecs.photoPompePrimaire.map(photo => (
                    <div key={photo.id} className="audit-photo-thumbnail">
                      <img src={photo.dataUrl} alt={photo.name} />
                      <button
                        className="audit-photo-delete-btn"
                        onClick={() => supprimerPhoto('photoPompePrimaire', photo.id)}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Photo pompe recyclage */}
            <div className="audit-form-group audit-form-group-multiple">
              <label className="audit-label">Photo pompe recyclage</label>
              <div className={`audit-photo-upload-wrapper ${isPhotoMissing('photoPompeRecyclage') ? 'error' : ''}`}>
                <input type="file" accept="image/*" capture="environment" id="upload-pompe-recyclage-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('photoPompeRecyclage')} />
                <input
                  type="file"
                  accept="image/*"
                  multiple
                  id="upload-pompe-recyclage"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload('photoPompeRecyclage')}
                />
                <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-pompe-recyclage-camera', 'upload-pompe-recyclage')}>
                  <div className="audit-photo-icon">📷</div>
                  <p>Ajouter photo</p>
                </label>
                <div className="audit-photo-tooltip">
                  <span className="audit-photo-tooltip-title">Pompe recyclage</span>
                  <span className="audit-photo-tooltip-desc">
                    {/* ——— NOUVEAU : info recyclage ——— */}
                    <img src="images/audit/hover/Recyclage-INFO.png" alt="" className='audit-photo-tooltip-img' />
                    Photographiez la pompe de bouclage ECS avec ses raccordements
                  </span>
                </div>
              </div>
              {formData.ecs.photoPompeRecyclage && formData.ecs.photoPompeRecyclage.length > 0 && (
                <div className="audit-photo-gallery">
                  {formData.ecs.photoPompeRecyclage.map(photo => (
                    <div key={photo.id} className="audit-photo-thumbnail">
                      <img src={photo.dataUrl} alt={photo.name} />
                      <button
                        className="audit-photo-delete-btn"
                        onClick={() => supprimerPhoto('photoPompeRecyclage', photo.id)}
                      >✕</button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Section optionnelle ECS */}
          <div className="audit-form-group audit-optional-section">
            <h3 
              className="audit-subsection-title" 
              onClick={() => setOptionalSectionOpen(!optionalSectionOpen)}
              style={{ cursor: 'pointer', userSelect: 'none' }}
              >
                Optionnel {optionalSectionOpen ? '▼' : '►'}
              </h3>
                  
              {optionalSectionOpen && (
                <>
              <div className="audit-form-group">
                <label className="audit-label">Type de système ECS (si connu)</label>
                <div className="audit-radio-group-vertical">
                  {equipements.typesEcs.map(type => (
                    <label key={type.id} className="audit-radio-label-vertical">
                      <input
                        type="radio"
                        name="typeEcs"
                        checked={formData.ecs.typeEcs === type.id}
                        onChange={() => setFormData(prev => ({
                          ...prev,
                          ecs: { ...prev.ecs, typeEcs: type.id }
                        }))}
                      />
                      <span className="audit-radio-text">{type.nom}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="audit-form-group">
                <div className="audit-label-with-info">
                  <label className="audit-label">Type de pompe primaire (si connu)</label>
                  <div
                    className="audit-info-wrapper"
                    onMouseEnter={() => setActiveTooltip('ecs-primaire-info')}
                    onMouseLeave={() => setActiveTooltip(null)}
                  >
                    <div className={`audit-photo-tooltip ${activeTooltip === 'ecs-primaire-info' ? 'active' : ''}`}>
                      <span className="audit-photo-tooltip-title">Identifier le type de pompe primaire</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/Pompe-double-ETI.png" alt="" className='audit-photo-tooltip-img' />
                        Repérez si la pompe est simple ou double corps, et notez le modèle indiqué sur son étiquette.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="audit-radio-group-vertical">
                  {equipements.pompesEcs.map(pompe => (
                    <label key={pompe.id} className="audit-radio-label-vertical">
                      <input
                        type="radio"
                        name="pompePrimaire"
                        checked={formData.ecs.pompePrimaire === pompe.id}
                        onChange={() => setFormData(prev => ({
                          ...prev,
                          ecs: { ...prev.ecs, pompePrimaire: pompe.id }
                        }))}
                      />
                      <span className="audit-radio-text">{pompe.nom}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="audit-form-group">
                <div className="audit-label-with-info">
                  <label className="audit-label">Type de pompe recyclage (si connu)</label>
                  <div
                    className="audit-info-wrapper"
                    onMouseEnter={() => setActiveTooltip('ecs-recyclage-info')}
                    onMouseLeave={() => setActiveTooltip(null)}
                  >
                    <div className={`audit-photo-tooltip ${activeTooltip === 'ecs-recyclage-info' ? 'active' : ''}`}>
                      <span className="audit-photo-tooltip-title">Identifier la pompe de recyclage</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/Recyclage-INFO.png" alt="" className='audit-photo-tooltip-img' style={{maxHeight:"300px",width:"auto"}}/>
                        La pompe de bouclage (recyclage) est généralement plus petite que la pompe primaire. Elle assure la circulation permanente de l'ECS dans la boucle.
                      </span>
                    </div>
                  </div>
                </div>
                <div className="audit-radio-group-vertical">
                  {equipements.pompesEcs.map(pompe => (
                    <label key={pompe.id} className="audit-radio-label-vertical">
                      <input
                        type="radio"
                        name="pompeRecyclage"
                        checked={formData.ecs.pompeRecyclage === pompe.id}
                        onChange={() => setFormData(prev => ({
                          ...prev,
                          ecs: { ...prev.ecs, pompeRecyclage: pompe.id }
                        }))}
                      />
                      <span className="audit-radio-text">{pompe.nom}</span>
                    </label>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>

            <div className="audit-form-group">
              <label className="audit-label">Autres photos ECS</label>
              <input type="file" accept="image/*" capture="environment" id="upload-ecs-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('ecs')} />
              <input
                type="file"
                accept="image/*"
                multiple
                id="upload-ecs"
                style={{ display: 'none' }}
                onChange={handlePhotoUpload('ecs')}
              />
              <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-ecs-camera', 'upload-ecs')}>
                <div className="audit-photo-icon">📷</div>
                <p>Cliquez pour ajouter des photos</p>
                <small>Max 10MB par photo - Format JPG, PNG</small>
              </label>

              {formData.ecs.photos && formData.ecs.photos.length > 0 && (
                <div className="audit-photo-gallery">
                  {formData.ecs.photos.map(photo => (
                    <div key={photo.id} className="audit-photo-thumbnail">
                      <img src={photo.dataUrl} alt={photo.name} />
                      <button
                        className="audit-photo-delete-btn"
                        onClick={() => supprimerPhoto('ecs', photo.id)}
                        title="Supprimer"
                      >
                        ✕
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <div className="audit-form-group">
              <label className="audit-label">Observations générales ECS</label>
              <textarea
                className="audit-textarea"
                value={formData.ecs.observations}
                onChange={(e) => setFormData(prev => ({
                  ...prev,
                  ecs: { ...prev.ecs, observations: e.target.value }
                }))}
                placeholder="Informations complémentaires..."
              />
            </div>
          </section>

          {/* SECTION 5 - ARMOIRE ÉLECTRIQUE */}
          <section className="audit-section" id="section-armoire" ref={sectionRefs.armoire}>
            <h2 className="audit-section-title">5. Armoire électrique</h2>
            
            <div className="audit-photos-required-group-horizontal">
              {/* Photo armoire avant */}
              <div className="audit-form-group audit-form-group-multiple">
                <label className="audit-label">Photo armoire électrique façade</label>
                <div className={`audit-photo-upload-wrapper ${isPhotoMissing('armoireElectriqueAvant') ? 'error' : ''}`}>
                  <input type="file" accept="image/*" capture="environment" id="upload-armoire-electrique-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('armoireElectrique')} />
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    id="upload-armoire-electrique"
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload('armoireElectrique')}
                  />
                  <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-armoire-electrique-camera', 'upload-armoire-electrique')}>
                    <div className="audit-photo-icon">📷</div>
                    <p>Ajouter photo</p>
                  </label>
                  <div className="audit-photo-tooltip">
                    <span className="audit-photo-tooltip-title">Façade armoire</span>
                    <span className="audit-photo-tooltip-desc">
                      {/* ——— NOUVEAU : armoire face ——— */}
                      <img src="images/audit/hover/ArmoireFace.jpg" alt="" className='audit-photo-tooltip-img' />
                      Photographiez la façade de l'armoire électrique avec la porte fermée
                    </span>
                  </div>
                </div>

                {formData.armoireElectriquePhotos && formData.armoireElectriquePhotos.length > 0 && (
                  <div className="audit-photo-gallery">
                    {formData.armoireElectriquePhotos.map(photo => (
                      <div key={photo.id} className="audit-photo-thumbnail">
                        <img src={photo.dataUrl} alt={photo.name} />
                        <button
                          className="audit-photo-delete-btn"
                          onClick={() => supprimerPhoto('armoireElectrique', photo.id)}
                          title="Supprimer"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Photo armoire intérieur */}
              <div className="audit-form-group audit-form-group-multiple">
                <label className="audit-label">Photo armoire électrique intérieur</label>
                <div className={`audit-photo-upload-wrapper ${isPhotoMissing('armoireElectriqueInterieur') ? 'error' : ''}`}>
                  <input type="file" accept="image/*" capture="environment" id="upload-armoire-electrique-interieur-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('armoireElectriqueInterieur')} />
                  <input
                    type="file"
                    accept="image/*"
                    multiple
                    id="upload-armoire-electrique-interieur"
                    style={{ display: 'none' }}
                    onChange={handlePhotoUpload('armoireElectriqueInterieur')}
                  />
                  <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-armoire-electrique-interieur-camera', 'upload-armoire-electrique-interieur')}>
                    <div className="audit-photo-icon">📷</div>
                    <p>Ajouter photo</p>
                  </label>
                  <div className="audit-photo-tooltip">
                    <span className="audit-photo-tooltip-title">Armoire intérieur</span>
                    <span className="audit-photo-tooltip-desc">
                      {/* ——— NOUVEAU : armoire intérieur ——— */}
                      <img src="images/audit/hover/ArmoireInterieur.jpg" alt="" className='audit-photo-tooltip-img' />
                      Photographiez le câblage à l'intérieur de l'armoire électrique
                    </span>
                  </div>
                </div>

                {formData.armoireElectriqueInterieurPhotos && formData.armoireElectriqueInterieurPhotos.length > 0 && (
                  <div className="audit-photo-gallery">
                    {formData.armoireElectriqueInterieurPhotos.map(photo => (
                      <div key={photo.id} className="audit-photo-thumbnail">
                        <img src={photo.dataUrl} alt={photo.name} />
                        <button
                          className="audit-photo-delete-btn"
                          onClick={() => supprimerPhoto('armoireElectriqueInterieur', photo.id)}
                          title="Supprimer"
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </section>

          {/* SECTION 6 - COMPTEUR GAZ */}
          <section className="audit-section" id="section-compteurs" ref={sectionRefs.compteurs}>
            <h2 className="audit-section-title">6. Compteurs</h2>

            {/* Message si aucune énergie sélectionnée */}
            {!formData.chaudiere.energie && (
              <div style={{
                textAlign: 'center', padding: '40px 20px',
                background: '#f8f9fa', borderRadius: '12px',
                border: '2px dashed #dee2e6', margin: '16px 0'
              }}>
                <p style={{ color: '#666', margin: 0, lineHeight: '1.6' }}>
                  Veuillez d'abord sélectionner un <strong>type d'énergie</strong> dans la section 2<br />
                  pour afficher les compteurs correspondants.
                </p>
              </div>
            )}

            {/* ── Compteur Gaz (gaz uniquement) ── */}
            {showCompteurGaz && (
              <div className="audit-circuit-card-compact">
                <div className="audit-circuit-header-compact">
                  <h3 style={{ margin: 0, color: '#034C8C', fontSize: '16px' }}>Compteur Gaz</h3>
                </div>

                {/* Photo */}
                <div className="audit-form-group">
                  <label className="audit-label">Photo du compteur (marque et référence)</label>
                  <div className="audit-photo-upload-wrapper">
                    <input type="file" accept="image/*" capture="environment" id="upload-compteur-gaz-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('compteurGaz')} />
                    <input type="file" accept="image/*" multiple id="upload-compteur-gaz" style={{ display: 'none' }} onChange={handlePhotoUpload('compteurGaz')} />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-compteur-gaz-camera', 'upload-compteur-gaz')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Cliquez pour ajouter des photos</p>
                      <small>Photographiez la plaque avec marque, modèle, numéro de série</small>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Compteur Gaz</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/CPT_GAZ.png" alt="" className="audit-photo-tooltip-img" />
                        Photographiez bien la plaque signalétique du compteur
                      </span>
                    </div>
                  </div>
                  {formData.compteurs.gaz.photos?.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.compteurs.gaz.photos.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button className="audit-photo-delete-btn" onClick={() => supprimerPhoto('compteurGaz', photo.id)}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                {/* Localisation */}
                <div className="audit-form-group">
                  <label className="audit-label">Le compteur est situé</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="gaz-localisation" checked={formData.compteurs.gaz.localisation === 'interieur'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, localisation: 'interieur' } } }))} />
                      Intérieur
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="gaz-localisation" checked={formData.compteurs.gaz.localisation === 'exterieur'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, localisation: 'exterieur' } } }))} />
                      Extérieur
                    </label>
                  </div>
                </div>

                {/* Distance */}
                <div className="audit-form-group">
                  <label className="audit-label">Distance par rapport à la chaufferie</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="number" className="audit-input" min="0" style={{ maxWidth: '180px' }} value={formData.compteurs.gaz.distance}
                      onChange={(e) => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, distance: e.target.value } } }))}
                      placeholder="Ex: 5" />
                    <span style={{ color: '#666', fontWeight: '500' }}>mètres</span>
                  </div>
                </div>

                {/* Câble de liaison */}
                <div className="audit-form-group">
                  <label className="audit-label">Peut-on relier par un câble la chaufferie principale au compteur ?</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="gaz-cable" checked={formData.compteurs.gaz.cableRelier === 'oui'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, cableRelier: 'oui' } } }))} />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="gaz-cable" checked={formData.compteurs.gaz.cableRelier === 'non'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, cableRelier: 'non' } } }))} />
                      Non
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="gaz-cable" checked={formData.compteurs.gaz.cableRelier === 'ne-sais-pas'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, cableRelier: 'ne-sais-pas' } } }))} />
                      Je ne sais pas
                    </label>
                  </div>
                </div>

                {/* Déjà connecté */}
                <div className="audit-form-group">
                  <label className="audit-label">Compteur déjà connecté ?</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="gaz-connecte" checked={formData.compteurs.gaz.dejaConnecte === 'oui'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, dejaConnecte: 'oui' } } }))} />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="gaz-connecte" checked={formData.compteurs.gaz.dejaConnecte === 'non'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, gaz: { ...prev.compteurs.gaz, dejaConnecte: 'non' } } }))} />
                      Non
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* ── Compteur CPCU (urbain uniquement) ── */}
            {showCompteurCpcu && (
              <div className="audit-circuit-card-compact">
                <div className="audit-circuit-header-compact">
                  <h3 style={{ margin: 0, color: '#034C8C', fontSize: '16px' }}>Compteur CPCU</h3>
                </div>
                <div className="audit-form-group">
                  <label className="audit-label">Photo du compteur (marque et référence)</label>
                  <div className="audit-photo-upload-wrapper">
                    <input type="file" accept="image/*" capture="environment" id="upload-compteur-cpcu-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('compteurCpcu')} />
                    <input type="file" accept="image/*" multiple id="upload-compteur-cpcu" style={{ display: 'none' }} onChange={handlePhotoUpload('compteurCpcu')} />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-compteur-cpcu-camera', 'upload-compteur-cpcu')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Cliquez pour ajouter des photos</p>
                      <small>Photographiez la plaque avec marque, modèle, numéro de série</small>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Compteur CPCU</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/CPT_CPCU.png" alt="" className="audit-photo-tooltip-img" />
                        Photographiez bien la plaque signalétique du compteur
                      </span>
                    </div>
                  </div>
                  {formData.compteurs.cpcu.photos?.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.compteurs.cpcu.photos.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button className="audit-photo-delete-btn" onClick={() => supprimerPhoto('compteurCpcu', photo.id)}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="audit-form-group">
                  <label className="audit-label">Distance par rapport à la chaufferie</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="number" className="audit-input" min="0" style={{ maxWidth: '180px' }} value={formData.compteurs.cpcu.distance}
                      onChange={(e) => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, cpcu: { ...prev.compteurs.cpcu, distance: e.target.value } } }))}
                      placeholder="Ex: 5" />
                    <span style={{ color: '#666', fontWeight: '500' }}>mètres</span>
                  </div>
                </div>

                {/* Câble de liaison */}
                <div className="audit-form-group">
                  <label className="audit-label">Peut-on relier par un câble la chaufferie principale au compteur ?</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="cpcu-cable" checked={formData.compteurs.cpcu.cableRelier === 'oui'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, cpcu: { ...prev.compteurs.cpcu, cableRelier: 'oui' } } }))} />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="cpcu-cable" checked={formData.compteurs.cpcu.cableRelier === 'non'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, cpcu: { ...prev.compteurs.cpcu, cableRelier: 'non' } } }))} />
                      Non
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="cpcu-cable" checked={formData.compteurs.cpcu.cableRelier === 'ne-sais-pas'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, cpcu: { ...prev.compteurs.cpcu, cableRelier: 'ne-sais-pas' } } }))} />
                      Je ne sais pas
                    </label>
                  </div>
                </div>

                <div className="audit-form-group">
                  <label className="audit-label">Compteur déjà connecté ?</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="cpcu-connecte" checked={formData.compteurs.cpcu.dejaConnecte === 'oui'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, cpcu: { ...prev.compteurs.cpcu, dejaConnecte: 'oui' } } }))} />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="cpcu-connecte" checked={formData.compteurs.cpcu.dejaConnecte === 'non'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, cpcu: { ...prev.compteurs.cpcu, dejaConnecte: 'non' } } }))} />
                      Non
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* ── Compteur d'Eau (fioul, urbain, autre) ── */}
            {showCompteurEau && (
              <div className="audit-circuit-card-compact">
                <div className="audit-circuit-header-compact">
                  <h3 style={{ margin: 0, color: '#034C8C', fontSize: '16px' }}>Compteur d'Eau</h3>
                </div>
                <div className="audit-form-group">
                  <label className="audit-label">Photo du compteur (marque et référence)</label>
                  <div className="audit-photo-upload-wrapper">
                    <input type="file" accept="image/*" capture="environment" id="upload-compteur-eau-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('compteurEau')} />
                    <input type="file" accept="image/*" multiple id="upload-compteur-eau" style={{ display: 'none' }} onChange={handlePhotoUpload('compteurEau')} />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-compteur-eau-camera', 'upload-compteur-eau')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Cliquez pour ajouter des photos</p>
                      <small>Photographiez la plaque avec marque, modèle, numéro de série</small>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Compteur d'Eau</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/CPT_EAU.png" alt="" className="audit-photo-tooltip-img" />
                        Photographiez bien la plaque signalétique du compteur
                      </span>
                    </div>
                  </div>
                  {formData.compteurs.eau.photos?.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.compteurs.eau.photos.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button className="audit-photo-delete-btn" onClick={() => supprimerPhoto('compteurEau', photo.id)}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="audit-form-group">
                  <label className="audit-label">Distance par rapport à la chaufferie</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="number" className="audit-input" min="0" style={{ maxWidth: '180px' }} value={formData.compteurs.eau.distance}
                      onChange={(e) => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, eau: { ...prev.compteurs.eau, distance: e.target.value } } }))}
                      placeholder="Ex: 5" />
                    <span style={{ color: '#666', fontWeight: '500' }}>mètres</span>
                  </div>
                </div>

                {/* Câble de liaison */}
                <div className="audit-form-group">
                  <label className="audit-label">Peut-on relier par un câble la chaufferie principale au compteur ?</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="eau-cable" checked={formData.compteurs.eau.cableRelier === 'oui'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, eau: { ...prev.compteurs.eau, cableRelier: 'oui' } } }))} />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="eau-cable" checked={formData.compteurs.eau.cableRelier === 'non'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, eau: { ...prev.compteurs.eau, cableRelier: 'non' } } }))} />
                      Non
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="eau-cable" checked={formData.compteurs.eau.cableRelier === 'ne-sais-pas'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, eau: { ...prev.compteurs.eau, cableRelier: 'ne-sais-pas' } } }))} />
                      Je ne sais pas
                    </label>
                  </div>
                </div>

                <div className="audit-form-group">
                  <label className="audit-label">Compteur déjà connecté ?</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="eau-connecte" checked={formData.compteurs.eau.dejaConnecte === 'oui'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, eau: { ...prev.compteurs.eau, dejaConnecte: 'oui' } } }))} />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="eau-connecte" checked={formData.compteurs.eau.dejaConnecte === 'non'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, eau: { ...prev.compteurs.eau, dejaConnecte: 'non' } } }))} />
                      Non
                    </label>
                  </div>
                </div>
              </div>
            )}

            {/* ── Compteur d'Énergie (fioul, urbain, autre) ── */}
            {showCompteurEnergie && (
              <div className="audit-circuit-card-compact">
                <div className="audit-circuit-header-compact">
                  <h3 style={{ margin: 0, color: '#034C8C', fontSize: '16px' }}>Compteur d'Énergie</h3>
                </div>
                <div className="audit-form-group">
                  <label className="audit-label">Photo du compteur (marque et référence)</label>
                  <div className="audit-photo-upload-wrapper">
                    <input type="file" accept="image/*" capture="environment" id="upload-compteur-energie-camera" style={{ display: 'none' }} onChange={handlePhotoUpload('compteurEnergie')} />
                    <input type="file" accept="image/*" multiple id="upload-compteur-energie" style={{ display: 'none' }} onChange={handlePhotoUpload('compteurEnergie')} />
                    <label className="audit-photo-upload" onClick={() => handlePhotoClick('upload-compteur-energie-camera', 'upload-compteur-energie')}>
                      <div className="audit-photo-icon">📷</div>
                      <p>Cliquez pour ajouter des photos</p>
                      <small>Photographiez la plaque avec marque, modèle, numéro de série</small>
                    </label>
                    <div className="audit-photo-tooltip">
                      <span className="audit-photo-tooltip-title">Compteur d'Énergie</span>
                      <span className="audit-photo-tooltip-desc">
                        <img src="images/audit/hover/CPT_ENER.png" alt="" className="audit-photo-tooltip-img" />
                        Photographiez bien la plaque signalétique du compteur
                      </span>
                    </div>
                  </div>
                  {formData.compteurs.energie.photos?.length > 0 && (
                    <div className="audit-photo-gallery">
                      {formData.compteurs.energie.photos.map(photo => (
                        <div key={photo.id} className="audit-photo-thumbnail">
                          <img src={photo.dataUrl} alt={photo.name} />
                          <button className="audit-photo-delete-btn" onClick={() => supprimerPhoto('compteurEnergie', photo.id)}>✕</button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
                <div className="audit-form-group">
                  <label className="audit-label">Distance par rapport à la chaufferie</label>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
                    <input type="number" className="audit-input" min="0" style={{ maxWidth: '180px' }} value={formData.compteurs.energie.distance}
                      onChange={(e) => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, energie: { ...prev.compteurs.energie, distance: e.target.value } } }))}
                      placeholder="Ex: 5" />
                    <span style={{ color: '#666', fontWeight: '500' }}>mètres</span>
                  </div>
                </div>

                {/* Câble de liaison */}
                <div className="audit-form-group">
                  <label className="audit-label">Peut-on relier par un câble la chaufferie principale au compteur ?</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="energie-cable" checked={formData.compteurs.energie.cableRelier === 'oui'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, energie: { ...prev.compteurs.energie, cableRelier: 'oui' } } }))} />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="energie-cable" checked={formData.compteurs.energie.cableRelier === 'non'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, energie: { ...prev.compteurs.energie, cableRelier: 'non' } } }))} />
                      Non
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="energie-cable" checked={formData.compteurs.energie.cableRelier === 'ne-sais-pas'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, energie: { ...prev.compteurs.energie, cableRelier: 'ne-sais-pas' } } }))} />
                      Je ne sais pas
                    </label>
                  </div>
                </div>

                <div className="audit-form-group">
                  <label className="audit-label">Compteur déjà connecté ?</label>
                  <div className="audit-radio-group">
                    <label className="audit-radio-label">
                      <input type="radio" name="energie-connecte" checked={formData.compteurs.energie.dejaConnecte === 'oui'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, energie: { ...prev.compteurs.energie, dejaConnecte: 'oui' } } }))} />
                      Oui
                    </label>
                    <label className="audit-radio-label">
                      <input type="radio" name="energie-connecte" checked={formData.compteurs.energie.dejaConnecte === 'non'} onChange={() => setFormData(prev => ({ ...prev, compteurs: { ...prev.compteurs, energie: { ...prev.compteurs.energie, dejaConnecte: 'non' } } }))} />
                      Non
                    </label>
                  </div>
                </div>
              </div>
            )}

          </section>

          {/* BOUTONS D'ACTION */}
          <div className="audit-actions">
            <button className="audit-btn audit-btn-primary" onClick={exporterPDF}>
              Télécharger PDF
            </button>
            <button className="audit-btn audit-btn-success" onClick={envoyerParEmail}>
              Envoyer par email
            </button>
            <button className="audit-btn audit-btn-secondary" onClick={nouveauAudit}>
              Nouvel audit
            </button>
          </div>

          <div className="audit-auto-save">
            💾 Vos données sont automatiquement sauvegardées
          </div>
        </div>

        {/* ACCOLADES DE SECTION */}
        <div className="audit-section-markers">
          {sectionPositions.site && (
            <div 
              className="audit-marker" 
              data-section="1"
              style={{
                top: `${sectionPositions.site.top}px`,
                height: `${sectionPositions.site.height}px`
              }}
            >
              <div className="audit-marker-brace">
                <div className="audit-marker-brace-line"></div>
              </div>
              <span className="audit-marker-label">Site</span>
            </div>
          )}
          {sectionPositions.chaudiere && (
            <div 
              className="audit-marker" 
              data-section="2"
              style={{
                top: `${sectionPositions.chaudiere.top}px`,
                height: `${sectionPositions.chaudiere.height}px`
              }}
            >
              <div className="audit-marker-brace">
                <div className="audit-marker-brace-line"></div>
              </div>
              <span className="audit-marker-label">Chaudière</span>
            </div>
          )}
          {sectionPositions.circuits && (
            <div 
              className="audit-marker" 
              data-section="3"
              style={{
                top: `${sectionPositions.circuits.top}px`,
                height: `${sectionPositions.circuits.height}px`
              }}
            >
              <div className="audit-marker-brace">
                <div className="audit-marker-brace-line"></div>
              </div>
              <span className="audit-marker-label">Circuits</span>
            </div>
          )}
          {sectionPositions.ecs && (
            <div 
              className="audit-marker" 
              data-section="4"
              style={{
                top: `${sectionPositions.ecs.top}px`,
                height: `${sectionPositions.ecs.height}px`
              }}
            >
              <div className="audit-marker-brace">
                <div className="audit-marker-brace-line"></div>
              </div>
              <span className="audit-marker-label">ECS</span>
            </div>
          )}
          {sectionPositions.armoire && (
          <div 
            className="audit-marker" 
            data-section="5"
            style={{
              top: `${sectionPositions.armoire.top}px`,
              height: `${sectionPositions.armoire.height}px`
            }}
          >
            <div className="audit-marker-brace">
              <div className="audit-marker-brace-line"></div>
            </div>
            <span className="audit-marker-label">Armoire</span>
          </div>
        )}
        {sectionPositions.compteurs && (
          <div
            className="audit-marker"
            data-section="6"
            style={{
              top: `${sectionPositions.compteurs.top}px`,
              height: `${sectionPositions.compteurs.height}px`
            }}
          >
            <div className="audit-marker-brace">
              <div className="audit-marker-brace-line"></div>
            </div>
            <span className="audit-marker-label">Compteurs</span>
          </div>
        )}
        </div>
      </div>

      {/* ACTION SHEET PHOTO (mobile) */}
      {photoMenu && (
        <div className="audit-photo-menu-overlay" onClick={() => setPhotoMenu(null)}>
          <div className="audit-photo-menu" onClick={e => e.stopPropagation()}>
            <button
              className="audit-photo-menu-btn"
              onClick={() => { document.getElementById(photoMenu.cameraId).click(); setPhotoMenu(null); }}
            >
              <span className="audit-photo-menu-icon">📷</span>
              <span>Appareil photo</span>
            </button>
            <button
              className="audit-photo-menu-btn"
              onClick={() => { document.getElementById(photoMenu.galleryId).click(); setPhotoMenu(null); }}
            >
              <span className="audit-photo-menu-icon">🖼️</span>
              <span>Galerie</span>
            </button>
            <button className="audit-photo-menu-cancel" onClick={() => setPhotoMenu(null)}>
              Annuler
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export default AuditChaufferieVertical;